import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Segmented,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CopyOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import {
  cancelAgentPublicLeadPaymentEntry,
  createAgentPublicLeadFromOrder,
  createAgentPublicLeadNote,
  createAgentPublicLeadPaymentEntry,
  deleteAgentPublicLead,
  fetchAgentPublicLead,
  fetchAgentPublicLeadFileBlobUrl,
  fetchAgentPublicLeadFiles,
  fetchAgentPublicLeadNotes,
  fetchAgentPublicLeadPaymentEntries,
  fetchAgentLeadOrderSummaryPdf,
  patchAgentPublicLead,
  sendAgentLeadOrderSummary,
  sendAgentPublicLeadFiles,
  uploadAgentPublicLeadFile,
} from "../../../services/publicSiteLeadsApi";
import { mergeCrmPaymentsReceived } from "./crmPaymentsReceivedMerge";
import { startCrmLeadOfferHandoff } from "../../../utils/crmLeadHandoff";
import {
  buildWhatsAppChatUrl,
  sanitizeWhatsAppPhone,
} from "../../../utils/whatsappDeepLink";
import {
  CRM_LEAD_STATUS_OPTIONS,
  formatLeadSourceType,
  formatPublicLeadCategory,
  isCrmDocketLead,
} from "./crmLeadFormatters";
import {
  buildLinkedBookingFromLdsOrder,
  extractOrderIdFromCrmLead,
  extractOpsRequestIdFromCrmLead,
  mergeCruiseOrderIntoLinkedBooking,
} from "./crmLinkedBooking";
import {
  fetchLdsOrderConfirmationPdf,
  fetchLdsOrderHotelVoucherPdf,
  fetchLdsOrderSummaryPdf,
  fetchYaadSarigReceipt,
  sendCrmOrderDocuments,
} from "./crmOrderConfirmationPdf";
import { CRM_LEAD_OFFER_NOTED_EVENT } from "../../../utils/crmLeadOfferNote";
import { getCruiseOrder, getLdsOrder } from "../../../services/fhpService";
import { extractLdsOrderRowFromFetchResponse } from "../../../utils/orders/extractLdsOrderRowFromResponse";
import { isAdminUser } from "../../../services/authService";
import {
  closeManoQuote,
  extractManoApiError,
  getManoQuote,
  listManoQuotes,
  prepareManoQuotePayment,
} from "../../../services/manoQuotesService";
import {
  buildManoCruisePaymentPath,
  MANO_CABIN_CATEGORY_LABELS,
} from "../ManoQuotes/manoQuoteShared";
import CrmEmbeddedOpsPane from "./CrmEmbeddedOpsPane";
import "./CrmLeadDetailView.css";

const STATUS_OPTIONS = CRM_LEAD_STATUS_OPTIONS;

const ORDER_SUMMARY_FORM_FIELDS = [
  { key: "passengers", label: "שמות הנוסעים", placeholder: "נוסע 1  שם מלא\nנוסע 2  שם מלא" },
  { key: "booking_ref", label: "מספר הזמנה" },
  { key: "order_date", label: "תאריך ביצוע הזמנה", placeholder: "DD/MM/YYYY" },
  { key: "travel_date", label: "תאריך הפלגה / צ'ק-אין", placeholder: "DD/MM/YYYY" },
  { key: "nights", label: "מספר לילות" },
  { key: "destination", label: "יעד" },
  {
    key: "itinerary_text",
    label: "פירוט המסלול",
    placeholder:
      "יום 1 · חיפה · יציאה 17:00\nיום 2 · לימסול · הגעה 08:00 · יציאה 18:00\nיום 3 · יום בים\nיום 4 · חיפה · הגעה 07:00",
    multiline: true,
    rows: 5,
  },
  { key: "room_type", label: "סוג חדר" },
  { key: "deck", label: "סיפון (קומה)" },
  { key: "board_basis", label: "בסיס אירוח" },
  { key: "price", label: 'מחיר עבור ההזמנה', placeholder: 'לדוגמה 3269 ש"ח' },
];

/** Default legal block embedded in Mano order-summary PDF (editable in the form). */
const DEFAULT_ORDER_SUMMARY_LEGAL_TEXT = [
  "סיכום הזמנה זה משקף את פרטי ההזמנה כפי שאושרו מול הסוכן.",
  "המחיר, המסלול, מועדי ההפלגה, סוג החדר והשירותים כפופים לתנאי מנו ספנות ולתנאי הספק, ולזמינות במועד ביצוע ההזמנה.",
  "מסלול ההפלגה וזמני העגינה עשויים להשתנות בהתאם להחלטת חברת השייט והרשויות.",
  "תנאי הביטול והשינוי בהתאם למדיניות מנו ספנות ולמדיניות הסוכנות, ויימסרו על ידי הסוכן.",
  "הסוכנות משמשת כמתווך בין הלקוח לבין ספקי השירות; אספקת השירותים, טיבם ואיכותם באחריות הספקים בלבד.",
].join("\n");

function formatManoItineraryText(stops) {
  if (!Array.isArray(stops) || !stops.length) return "";
  return stops
    .map((stop) => {
      if (!stop || typeof stop !== "object") return "";
      const day = stop.day != null ? `יום ${stop.day}` : "";
      const datePart = stop.date
        ? String(stop.date).slice(0, 10).split("-").reverse().join("/")
        : "";
      const port = stop.is_sea_day
        ? "יום בים"
        : [stop.port, stop.country ? `(${stop.country})` : ""]
            .filter(Boolean)
            .join(" ");
      const times = [];
      if (stop.arrival_time) times.push(`הגעה ${stop.arrival_time}`);
      if (stop.departure_time) times.push(`יציאה ${stop.departure_time}`);
      return [day, datePart, port, times.join(" · ")].filter(Boolean).join(" · ");
    })
    .filter(Boolean)
    .join("\n");
}

function parseManoQuoteIdFromLead(leadRow) {
  const linked = leadRow?.linked_booking || null;
  const fromBooking = Number(linked?.mano_quote_id);
  if (Number.isFinite(fromBooking) && fromBooking > 0) return fromBooking;
  const cruise = linked?.cruise || linked?.cruiseInfo || null;
  const fromCruise = Number(cruise?.mano_quote_id);
  if (Number.isFinite(fromCruise) && fromCruise > 0) return fromCruise;
  const sourceKey = String(leadRow?.source_key || "").trim();
  const m = sourceKey.match(/^mano_quote:(\d+)/i);
  if (m) {
    const id = Number(m[1]);
    if (Number.isFinite(id) && id > 0) return id;
  }
  const notesText = String(
    linked?.notes || cruise?.notes || linked?.order_notes || ""
  );
  const fromNotes = notesText.match(/mano_quote_id\s*=\s*(\d+)/i);
  if (fromNotes?.[1]) {
    const id = Number(fromNotes[1]);
    if (Number.isFinite(id) && id > 0) return id;
  }
  const orderNumber = String(
    linked?.order_number || linked?.order_no || ""
  ).trim();
  const fromOrder = orderNumber.match(/^MANO-Q-(\d+)$/i);
  if (fromOrder?.[1]) {
    const id = Number(fromOrder[1]);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

function isManoCruiseLinkedBooking(linkedBooking) {
  if (!linkedBooking || typeof linkedBooking !== "object") return false;
  const ot = String(linkedBooking.order_type || "").trim();
  return (
    ot === "cruise_orders" ||
    ot === "mano_quote" ||
    Boolean(linkedBooking.mano_quote_id)
  );
}

function isoToDdMmYyyy(raw) {
  if (!raw) return "";
  const s = String(raw).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  return String(raw).trim();
}

function buildDefaultOrderSummaryFields(leadRow) {
  // PDF brands with agency logo/details from the server — do not send personal agent name.
  const linked = leadRow?.linked_booking || null;
  const destination =
    String(
      linked?.hotel_name ||
        linked?.interest_destination ||
        leadRow?.interest_destination ||
        ""
    ).trim() || "";
  const guest = String(
    linked?.guest_name || leadRow?.full_name || ""
  ).trim();
  const today = new Date();
  const orderDate = `${String(today.getDate()).padStart(2, "0")}/${String(
    today.getMonth() + 1
  ).padStart(2, "0")}/${today.getFullYear()}`;
  const travelDate = isoToDdMmYyyy(linked?.dep_date);
  const nights =
    linked?.nights != null && String(linked.nights).trim()
      ? String(linked.nights)
      : "";
  const bookingRef = String(linked?.booking_ref || "").trim();
  const priceRaw = String(linked?.amount_display || "").trim();
  const price =
    priceRaw && priceRaw !== "—"
      ? priceRaw.includes("ש") || priceRaw.includes("₪")
        ? priceRaw
        : `${priceRaw} ש"ח`
      : "";
  return {
    passengers: guest ? `נוסע 1  ${guest}` : "",
    booking_ref: bookingRef,
    order_date: orderDate,
    travel_date: travelDate,
    nights,
    destination,
    itinerary_text: "",
    sailing_id: "",
    room_type: "",
    deck: "",
    board_basis: "",
    price,
    legal_text: DEFAULT_ORDER_SUMMARY_LEGAL_TEXT,
  };
}

/** Merge Mano quote sailing details into order-summary form fields. */
function coerceManoItineraryList(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((item) => item && typeof item === "object");
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && typeof item === "object")
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseManoQuoteSnapshot(detail) {
  let snap = detail?.preview_snapshot || detail?.snapshot_json || null;
  if (typeof snap === "string" && snap.trim()) {
    try {
      snap = JSON.parse(snap);
    } catch {
      snap = null;
    }
  }
  return snap && typeof snap === "object" ? snap : null;
}

function mapRawManoQuoteRoomForOrderSummary(room, idx) {
  if (!room || typeof room !== "object") return null;
  const category = String(room.cabin_category || "").trim();
  const deckRaw = room.deck != null ? String(room.deck).trim() : "";
  let cabinName = String(
    room.cabin_name ||
      room.custom_cabin_name ||
      room.cabin_name_he ||
      room.cabin_type_name ||
      ""
  ).trim();
  if (!cabinName) {
    cabinName = `חדר ${room.room_number != null ? room.room_number : idx + 1}`;
  }
  if (deckRaw && !cabinName.includes("סיפון")) {
    cabinName = `${cabinName} — סיפון ${deckRaw}`;
  }
  const childrenAges = Array.isArray(room.children_ages)
    ? room.children_ages
    : Array.isArray(room.children_ages_json)
      ? room.children_ages_json
      : [];
  const infantsAges = Array.isArray(room.infants_ages)
    ? room.infants_ages
    : Array.isArray(room.infants_ages_json)
      ? room.infants_ages_json
      : [];
  return {
    room_number: Number(room.room_number) > 0 ? Number(room.room_number) : idx + 1,
    quantity: Number(room.quantity) > 0 ? Number(room.quantity) : 1,
    cabin_name: cabinName,
    deck: deckRaw || null,
    cabin_category: category || null,
    cabin_category_label:
      String(room.cabin_category_label || "").trim() ||
      (category ? MANO_CABIN_CATEGORY_LABELS[category] || category : ""),
    cabin_description: String(room.cabin_description_he || room.cabin_description || "").trim() || null,
    has_window: Boolean(room.has_window),
    has_balcony: Boolean(room.has_balcony),
    adults: Number(room.adults) || 0,
    children_ages: childrenAges,
    infants_ages: infantsAges,
    accessible_requested: Boolean(room.accessible_requested),
    room_price: room.room_price ?? null,
    room_note: String(room.room_note || "").trim() || null,
  };
}

function coerceManoOrderSummaryRooms(detail, snap) {
  const snapRooms = Array.isArray(snap?.rooms) ? snap.rooms : [];
  if (snapRooms.length) {
    return snapRooms
      .map((room, idx) => mapRawManoQuoteRoomForOrderSummary(room, idx))
      .filter(Boolean);
  }
  const detailRooms = Array.isArray(detail?.rooms) ? detail.rooms : [];
  return detailRooms
    .map((room, idx) => mapRawManoQuoteRoomForOrderSummary(room, idx))
    .filter(Boolean);
}

function applyManoQuoteToOrderSummaryFields(baseFields, detail, quoteHint) {
  const next = { ...(baseFields || {}) };
  const snap = parseManoQuoteSnapshot(detail);
  const sailing =
    snap?.sailing || detail?.sailing || null;
  if (sailing && typeof sailing === "object") {

  const itinerary = coerceManoItineraryList(
    sailing.itinerary ?? sailing.itinerary_json ?? detail?.itinerary
  );
  const destinationsText = String(sailing.destinations_text || "").trim();
  const nightsVal =
    sailing.nights != null && String(sailing.nights).trim()
      ? String(sailing.nights)
      : "";
  const travelDate = isoToDdMmYyyy(sailing.departure_date);

  if (destinationsText && !String(next.destination || "").trim()) {
    next.destination = destinationsText;
  }
  if (nightsVal && !String(next.nights || "").trim()) {
    next.nights = nightsVal;
  }
  if (travelDate && !String(next.travel_date || "").trim()) {
    next.travel_date = travelDate;
  }
  if (itinerary.length) {
    next.itinerary = itinerary;
    if (!String(next.itinerary_text || "").trim()) {
      next.itinerary_text = formatManoItineraryText(itinerary);
    }
  } else {
    // Omit empty list so the BE can resolve itinerary via sailing_id.
    delete next.itinerary;
  }
  const sid = detail?.sailing_id || quoteHint?.sailing_id || sailing.id || "";
  if (sid) next.sailing_id = String(sid);
  }

  const quoteId = Number(detail?.id || quoteHint?.id);
  if (Number.isFinite(quoteId) && quoteId > 0) {
    next.mano_quote_id = quoteId;
  }

  const orderSummaryRooms = coerceManoOrderSummaryRooms(detail, snap);
  if (orderSummaryRooms.length) {
    next.rooms = orderSummaryRooms;
    const first = orderSummaryRooms[0];
    if (first?.cabin_name && !String(next.room_type || "").trim()) {
      next.room_type = String(first.cabin_name).split(" — סיפון")[0].trim();
    }
    if (first?.deck && !String(next.deck || "").trim()) {
      next.deck = String(first.deck);
    }
  }

  const cabin =
    snap?.selected_cabin ||
    snap?.cabin ||
    detail?.selected_cabin ||
    null;
  if (cabin && typeof cabin === "object") {
    const roomType = String(cabin.room_type || cabin.name || cabin.category || "").trim();
    const deck = String(cabin.deck || cabin.deck_name || "").trim();
    const board = String(cabin.board_basis || cabin.board || "").trim();
    if (roomType && !String(next.room_type || "").trim()) next.room_type = roomType;
    if (deck && !String(next.deck || "").trim()) next.deck = deck;
    if (board && !String(next.board_basis || "").trim()) next.board_basis = board;
  }

  const quotePrice =
    detail?.total_price_ils ??
    detail?.price_ils ??
    snap?.total_price_ils ??
    null;
  if (quotePrice != null && !String(next.price || "").trim()) {
    const n = Number(quotePrice);
    if (Number.isFinite(n)) {
      next.price = `₪${n.toLocaleString("he-IL", {
        maximumFractionDigits: 2,
      })}`;
    }
  }

  const refHint =
    detail?.order_number ||
    detail?.booking_ref ||
    (detail?.id != null ? `MANO-Q-${detail.id}` : "") ||
    (quoteHint?.id != null ? `MANO-Q-${quoteHint.id}` : "");
  if (refHint && !String(next.booking_ref || "").trim()) {
    next.booking_ref = String(refHint);
  }

  return next;
}

/** Above `.crm-lead-detail` (fixed z-index 1200) so Ant Design portals stay visible */
const CRM_DETAIL_OVERLAY_Z = 1300;

/** Gate WhatsApp conversation pane until redesign — keep code paths, hide tab. */
const SHOW_CRM_WHATSAPP_PANE = false;

const LEFT_PANE_DOCS = "docs";
const LEFT_PANE_ACTIONS = "actions";
const LEFT_PANE_OPS = "ops";
const LEFT_PANE_WHATSAPP = "whatsapp";
/** Mobile-only: full-screen docket/summary column */
const LEFT_PANE_SUMMARY = "summary";

function resolveLeftPaneMode(initialLeftPane, initialOpsRequestId) {
  const pane = String(initialLeftPane || "").trim().toLowerCase();
  if (pane === LEFT_PANE_OPS || String(initialOpsRequestId || "").trim()) {
    return LEFT_PANE_OPS;
  }
  if (pane === LEFT_PANE_ACTIONS) return LEFT_PANE_ACTIONS;
  return LEFT_PANE_DOCS;
}

const CRM_MOBILE_MQ = "(max-width: 900px)";

function useCrmMobileLayout() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(CRM_MOBILE_MQ).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(CRM_MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function formatShortDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/** Parse DD/MM/YYYY or ISO-ish stay date → Date or null */
function parseStayDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatStayDateWithWeekday(raw) {
  const d = parseStayDate(raw);
  if (!d) return String(raw || "").trim() || "—";
  try {
    return d.toLocaleDateString("he-IL", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(raw);
  }
}

function buildStayDatesWithWeekdays(booking) {
  if (!booking) return "—";
  const dep = booking.dep_date || "";
  const ret = booking.ret_date || "";
  if (dep || ret) {
    return `${formatStayDateWithWeekday(dep)} – ${formatStayDateWithWeekday(ret)}`;
  }
  const label = String(booking.dates_label || "").trim();
  if (!label) return "—";
  const parts = label.split(/\s*[–—-]\s*/);
  if (parts.length >= 2) {
    return `${formatStayDateWithWeekday(parts[0])} – ${formatStayDateWithWeekday(parts[1])}`;
  }
  return label;
}

function formatFileRef(lead) {
  const id = String(lead?.id || "").replace(/-/g, "");
  if (id.length >= 6) return `HR-${id.slice(0, 6).toUpperCase()}`;
  return "HR-LEAD";
}

/** True when CRM lead copy still has generic hotel / wrong total placeholders. */
function isStaleCrmLeadCopy(text, liveAmountDisplay) {
  const t = String(text || "");
  if (!t.trim()) return true;
  if (t.includes(" · מלון · ") || t.startsWith("הזמנה אוטומטית · מלון")) return true;
  if (t.includes("מלון: מלון") || t.includes("יעד / מלון: מלון")) return true;
  const liveAmount = String(liveAmountDisplay || "")
    .split("(")[0]
    .trim();
  if (liveAmount && /סכום\s*:/.test(t) && !t.includes(liveAmount)) return true;
  return false;
}

/** Merge lead seed note (agent_internal_note) with API notes — never hide seed when API notes exist. */
function mergeLeadNotes(apiNotes, seed, leadId) {
  const list = Array.isArray(apiNotes) ? apiNotes : [];
  const liveAmount =
    seed?.linked_booking?.amount_display || seed?.amount_display || "";
  let seedText = String(seed?.agent_internal_note || "").trim();
  const liveSeed = String(seed?.linked_booking?.agent_internal_note || "").trim();
  if (liveSeed && (isStaleCrmLeadCopy(seedText, liveAmount) || !seedText)) {
    seedText = liveSeed;
  }
  if (!seedText) return list;
  const already = list.some(
    (n) => String(n?.note_text || "").trim() === seedText
  );
  if (already) return list;
  // Drop older stale auto-seed notes that duplicate this system note
  const filtered = list.filter((n) => {
    const text = String(n?.note_text || "");
    if (!String(n?.id || "").startsWith("legacy-")) return true;
    return !isStaleCrmLeadCopy(text, liveAmount);
  });
  return [
    {
      id: `legacy-${leadId}`,
      note_text: seedText,
      created_at: seed?.updated_at || seed?.created_at || null,
      note_type: "system_seed",
    },
    ...filtered,
  ];
}

/**
 * Customer offer handoff content (PDF / WhatsApp / digital link) — תיעוד correspondence.
 * Kept out of פעולות בדוקט even if a note_type like "offer" is present.
 */
function isCrmCustomerOfferCorrespondenceNote(note) {
  const text = String(note?.note_text || "").trim();
  if (!text) return false;
  const firstLine = text.split(/\r?\n/, 1)[0].trim();
  return (
    firstLine.startsWith("הצעה ללקוח מה-CRM") ||
    text.startsWith("הצעה ללקוח מה-CRM")
  );
}

/** CRM action-audit / system activity — belongs in פעולות בדוקט, not תיעוד correspondence. */
const CRM_ACTION_NOTE_MARKERS = [
  "תיק נפתח אוטומטית",
  "הצעת מנו נסגרה",
  "שליחת מסמכים מה-CRM",
  "שליחת הזמנה למלון",
  "הועלה קובץ",
  "התחיל יצירת הצעה",
  "הזמנה אוטומטית",
  "תשלום שהתקבל עודכן בדוקט",
  "בוטל תשלום שהתקבל בדוקט",
  "הוסתר תשלום מההזמנה בדוקט",
  "בוטל הסתרת תשלום מההזמנה בדוקט",
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "bank_transfer", label: "העברה בנקאית" },
  { value: "credit_card", label: "אשראי" },
  { value: "other", label: "אחר" },
];

const PAYMENT_DIRECTION_OPTIONS = [
  { value: "credit", label: "זכות (התקבל)" },
  { value: "debit", label: "חובה (החזר / תיקון)" },
];

const CRM_ACTION_NOTE_TYPES = new Set([
  "action",
  "system",
  "system_seed",
  "audit",
  "auto",
  "file_upload",
  "document_send",
  "offer_handoff",
]);

function isCrmActionAuditNote(note) {
  if (!note || typeof note !== "object") return false;
  // Offer-to-customer bubbles belong under תיעוד (human correspondence thread).
  if (isCrmCustomerOfferCorrespondenceNote(note)) return false;

  const id = String(note.id || "");
  if (id.startsWith("legacy-")) return true;

  const typed = String(
    note.note_type || note.source || note.kind || note.note_source || ""
  )
    .trim()
    .toLowerCase();
  if (typed && CRM_ACTION_NOTE_TYPES.has(typed)) return true;

  const text = String(note.note_text || "").trim();
  if (!text) return false;
  const firstLine = text.split(/\r?\n/, 1)[0].trim();
  return CRM_ACTION_NOTE_MARKERS.some(
    (marker) =>
      firstLine.startsWith(marker) ||
      text.startsWith(marker) ||
      (marker === "תיק נפתח אוטומטית" && text.includes("תיק נפתח אוטומטית מהזמנה"))
  );
}

/**
 * Full-screen lead file: documentation (left) + customer docket (right).
 */
export default function CrmLeadDetailView({
  leadId,
  initialLead = null,
  initialLeftPane = null,
  initialOpsType = null,
  initialOpsRequestId = null,
  backLabel = null,
  showCreateOffer = true,
  onClose,
  onDeleted,
  onLeadUpdated,
}) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [lead, setLead] = useState(initialLead);
  const [loading, setLoading] = useState(!initialLead);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [confirmationPdfUrl, setConfirmationPdfUrl] = useState(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState(null);
  const [hotelVoucherPdfUrl, setHotelVoucherPdfUrl] = useState(null);
  const [hotelVoucherLoading, setHotelVoucherLoading] = useState(false);
  const [hotelVoucherError, setHotelVoucherError] = useState(null);
  const [orderSummaryPdfUrl, setOrderSummaryPdfUrl] = useState(null);
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(false);
  const [orderSummaryError, setOrderSummaryError] = useState(null);
  const [orderSummaryModalOpen, setOrderSummaryModalOpen] = useState(false);
  const [orderSummaryFields, setOrderSummaryFields] = useState(() =>
    buildDefaultOrderSummaryFields(null)
  );
  const [orderSummarySendEmail, setOrderSummarySendEmail] = useState("");
  const [orderSummaryBusy, setOrderSummaryBusy] = useState(null); // 'preview' | 'download' | 'send' | null
  const [manoQuotesForLead, setManoQuotesForLead] = useState([]);
  const [manoQuotesLoading, setManoQuotesLoading] = useState(false);
  const [manoCloseBusyId, setManoCloseBusyId] = useState(null);
  const [manoPayBusyId, setManoPayBusyId] = useState(null);
  const [docPreviewTab, setDocPreviewTab] = useState("receipt");
  const [emailRows, setEmailRows] = useState([""]);
  const [sendSaving, setSendSaving] = useState(null); // 'confirmation' | 'receipt' | 'hotel_voucher' | 'order_summary' | 'bundle' | null
  const [sendFilesModalOpen, setSendFilesModalOpen] = useState(false);
  const [sendFilesSelection, setSendFilesSelection] = useState([
    "confirmation",
    "receipt",
    "hotel_voucher",
    "order_summary",
  ]);
  const [leftPaneMode, setLeftPaneMode] = useState(() =>
    resolveLeftPaneMode(initialLeftPane, initialOpsRequestId)
  ); // docs | actions | ops | whatsapp(gated)
  const [waComposerText, setWaComposerText] = useState("");
  const [waComposerSaving, setWaComposerSaving] = useState(false);
  const [contactEditing, setContactEditing] = useState(false);
  const [contactPhoneDraft, setContactPhoneDraft] = useState("");
  const [contactEmailDraft, setContactEmailDraft] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [extraFiles, setExtraFiles] = useState([]);
  const [extraFilesLoading, setExtraFilesLoading] = useState(false);
  const [extraFileUploading, setExtraFileUploading] = useState(false);
  const [composerDragOver, setComposerDragOver] = useState(false);
  const [selectedExtraFileId, setSelectedExtraFileId] = useState(null);
  const [extraFilePreviewUrl, setExtraFilePreviewUrl] = useState(null);
  const [extraFilePreviewLoading, setExtraFilePreviewLoading] = useState(false);
  const extraFilePreviewUrlRef = useRef(null);
  const fileInputRef = useRef(null);
  const [paymentEntries, setPaymentEntries] = useState([]);
  const [paymentEntriesLoading, setPaymentEntriesLoading] = useState(false);
  const [paymentEntrySaving, setPaymentEntrySaving] = useState(false);
  const [paymentAddOpen, setPaymentAddOpen] = useState(false);
  const [paymentFormMethod, setPaymentFormMethod] = useState("bank_transfer");
  const [paymentFormDirection, setPaymentFormDirection] = useState("credit");
  const [paymentFormAmount, setPaymentFormAmount] = useState("");
  const [paymentFormNote, setPaymentFormNote] = useState("");
  const [paymentFormDate, setPaymentFormDate] = useState("");
  const [paymentRowBusyKey, setPaymentRowBusyKey] = useState(null);

  const clientEmail = useMemo(() => {
    const fromLead = String(lead?.email || "").trim();
    if (fromLead) return fromLead;
    return String(lead?.linked_booking?.email || "").trim();
  }, [lead?.email, lead?.linked_booking?.email]);

  const onLeadUpdatedRef = useRef(onLeadUpdated);
  const onCloseRef = useRef(onClose);
  const initialLeadRef = useRef(initialLead);
  const notesFetchedForLeadRef = useRef("");
  const messageRef = useRef(message);
  onLeadUpdatedRef.current = onLeadUpdated;
  onCloseRef.current = onClose;
  initialLeadRef.current = initialLead;
  messageRef.current = message;

  const statusLabel = useMemo(() => {
    const key = lead?.status || "new";
    return STATUS_OPTIONS.find((s) => s.value === key)?.label || key;
  }, [lead?.status]);

  const sourceMeta = useMemo(() => formatLeadSourceType(lead || {}), [lead]);

  const linkedBooking = lead?.linked_booking || null;
  const isManoCruiseBooking = isManoCruiseLinkedBooking(linkedBooking);
  const manoQuoteId = useMemo(() => parseManoQuoteIdFromLead(lead), [lead]);
  const resolvedManoQuoteId = useMemo(() => {
    if (manoQuoteId) return manoQuoteId;
    const fromList = Number(manoQuotesForLead?.[0]?.id);
    return Number.isFinite(fromList) && fromList > 0 ? fromList : null;
  }, [manoQuoteId, manoQuotesForLead]);
  const paymentReceipt = linkedBooking?.payment_receipt || null;
  const orderPaymentsReceived = Array.isArray(linkedBooking?.payments_received)
    ? linkedBooking.payments_received
    : [];
  const paymentsReceived = useMemo(
    () => mergeCrmPaymentsReceived(orderPaymentsReceived, paymentEntries),
    [orderPaymentsReceived, paymentEntries]
  );
  // Cruise / Mano ids must not be treated as LDS order ids (receipts / Yaad / LDS PDFs).
  const linkedOrderId = isManoCruiseBooking
    ? null
    : linkedBooking?.order_id || null;
  const bookingRooms = Array.isArray(linkedBooking?.rooms)
    ? linkedBooking.rooms
    : [];
  const bookingFlights = Array.isArray(linkedBooking?.flights)
    ? linkedBooking.flights
    : [];
  const heroAmountLabel =
    paymentReceipt?.hero_amount_label ||
    linkedBooking?.hero_amount_label ||
    (paymentReceipt?.payment_complete === false
      ? "יתרה לתשלום"
      : "סכום סופי ששולם");
  const heroAmountDisplay =
    paymentReceipt?.hero_amount_display ||
    linkedBooking?.hero_amount_display ||
    (paymentReceipt?.payment_complete === false
      ? paymentReceipt?.balance_due_display
      : null) ||
    paymentReceipt?.total_due_display ||
    linkedBooking?.amount_display ||
    "—";
  const displayPhone = lead?.phone || linkedBooking?.phone || "";
  const displayEmail = lead?.email || linkedBooking?.email || "";
  const contactMissing = !String(displayPhone || "").trim() || !String(displayEmail || "").trim();
  const canUploadExtraFiles = isAdminUser();
  /** CRM payments-received add/hide/cancel — platform admin only (same gate as PDF upload). */
  const canManagePaymentEntries = canUploadExtraFiles;

  const mergeLeadPreserveBooking = useCallback((prev, next) => {
    if (!next || typeof next !== "object") return prev || next;
    // Prefer live order SSOT rebuild over stale server linked_booking
    if (next.linked_booking?.ssot) return next;
    if (next.linked_booking?.order_id && next.linked_booking?.payment_complete != null) {
      return next;
    }
    if (
      (prev?.linked_booking?.ssot || prev?.linked_booking?.order_id) &&
      String(prev.id) === String(next.id)
    ) {
      return {
        ...next,
        linked_booking: prev.linked_booking,
        source_type: next.source_type || prev.source_type || "completed_order",
      };
    }
    return next;
  }, []);

  const loadReceiptPdf = useCallback(async () => {
    if (!linkedOrderId) return null;
    setReceiptLoading(true);
    setReceiptError(null);
    try {
      const result = await fetchYaadSarigReceipt(linkedOrderId);
      if (result?.redirectUrl) {
        setReceiptPdfUrl((prev) => {
          if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
          return result.redirectUrl;
        });
        return result.redirectUrl;
      }
      if (!result?.blob) {
        throw new Error("empty_receipt");
      }
      const url = URL.createObjectURL(result.blob);
      setReceiptPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
      return url;
    } catch (err) {
      console.error(err);
      const hypMsg =
        err?.hypStatus ||
        (typeof err?.message === "string" && err.message.includes("יעד")
          ? err.message
          : null);
      setReceiptPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setReceiptError(
        hypMsg ||
          "לא נמצאה קבלה מיעד שריג להזמנה זו. (אישור ההזמנה של HotelsRiver הוא מסמך נפרד.)"
      );
      return null;
    } finally {
      setReceiptLoading(false);
    }
  }, [linkedOrderId]);

  const loadConfirmationPdf = useCallback(async () => {
    if (!linkedOrderId) return null;
    setConfirmationLoading(true);
    setConfirmationError(null);
    try {
      const blob = await fetchLdsOrderConfirmationPdf(linkedOrderId);
      const url = URL.createObjectURL(blob);
      setConfirmationPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
      return url;
    } catch (err) {
      console.error(err);
      setConfirmationPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setConfirmationError("טעינת אישור ההזמנה נכשלה");
      return null;
    } finally {
      setConfirmationLoading(false);
    }
  }, [linkedOrderId]);

  const loadHotelVoucherPdf = useCallback(async () => {
    if (!linkedOrderId) return null;
    setHotelVoucherLoading(true);
    setHotelVoucherError(null);
    try {
      const blob = await fetchLdsOrderHotelVoucherPdf(linkedOrderId);
      const url = URL.createObjectURL(blob);
      setHotelVoucherPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
      return url;
    } catch (err) {
      console.error(err);
      setHotelVoucherPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setHotelVoucherError(
        err?.message && String(err.message).length < 160
          ? String(err.message)
          : "שובר מלון לא זמין להזמנה זו"
      );
      return null;
    } finally {
      setHotelVoucherLoading(false);
    }
  }, [linkedOrderId]);

  const resolveManoOrderSummaryFields = useCallback(async () => {
    let fields = buildDefaultOrderSummaryFields(lead);
    const quoteId = resolvedManoQuoteId;
    if (!quoteId) return fields;
    try {
      const res = await getManoQuote(quoteId);
      const detail = res?.quote || res;
      const hint =
        (Array.isArray(manoQuotesForLead) &&
          manoQuotesForLead.find((q) => Number(q?.id) === Number(quoteId))) ||
        { id: quoteId };
      fields = applyManoQuoteToOrderSummaryFields(fields, detail, hint);
    } catch (err) {
      console.warn("[CrmLeadDetail] mano order-summary fields failed", err);
    }
    return fields;
  }, [lead, resolvedManoQuoteId, manoQuotesForLead]);

  const loadOrderSummaryPdf = useCallback(async () => {
    const useManoManual =
      Boolean(leadId) &&
      (isManoCruiseBooking || Boolean(manoQuoteId)) &&
      !linkedOrderId;
    if (!linkedOrderId && !useManoManual) return null;
    setOrderSummaryLoading(true);
    setOrderSummaryError(null);
    try {
      let blob;
      if (useManoManual) {
        const fields = await resolveManoOrderSummaryFields();
        blob = await fetchAgentLeadOrderSummaryPdf(leadId, fields);
        setOrderSummaryFields(fields);
      } else {
        blob = await fetchLdsOrderSummaryPdf(linkedOrderId);
      }
      const url = URL.createObjectURL(blob);
      setOrderSummaryPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
      return url;
    } catch (err) {
      console.error(err);
      setOrderSummaryPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setOrderSummaryError(
        err?.message && String(err.message).length < 160
          ? String(err.message)
          : "טעינת סיכום ההזמנה נכשלה"
      );
      return null;
    } finally {
      setOrderSummaryLoading(false);
    }
  }, [
    linkedOrderId,
    leadId,
    isManoCruiseBooking,
    manoQuoteId,
    resolveManoOrderSummaryFields,
  ]);

  const emailSeededRef = useRef(false);

  useEffect(() => {
    // Seed primary row once when client email becomes available; never overwrite edits.
    setEmailRows((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        emailSeededRef.current = Boolean(clientEmail);
        return [clientEmail || ""];
      }
      if (
        !emailSeededRef.current &&
        clientEmail &&
        prev.length === 1 &&
        !String(prev[0] || "").trim()
      ) {
        emailSeededRef.current = true;
        return [clientEmail];
      }
      if (clientEmail) emailSeededRef.current = true;
      return prev;
    });
  }, [clientEmail]);

  const addEmailRow = useCallback(() => {
    setEmailRows((prev) => [...(prev.length ? prev : [clientEmail || ""]), ""]);
  }, [clientEmail]);

  const updateEmailRow = useCallback((index, value) => {
    setEmailRows((prev) => {
      const next = [...(prev.length ? prev : [""])];
      next[index] = value;
      return next;
    });
  }, []);

  const removeEmailRow = useCallback((index) => {
    setEmailRows((prev) => {
      const list = prev.length ? [...prev] : [""];
      if (index <= 0) return list;
      list.splice(index, 1);
      return list.length ? list : [""];
    });
  }, []);

  const applyClientEmailToPrimary = useCallback(() => {
    if (!clientEmail) return;
    setEmailRows((prev) => {
      const list = prev.length ? [...prev] : [""];
      list[0] = clientEmail;
      return list;
    });
  }, [clientEmail]);

  const copyClientEmail = useCallback(async () => {
    if (!clientEmail) return;
    try {
      await navigator.clipboard.writeText(clientEmail);
      message.success("מייל הלקוח הועתק");
    } catch {
      message.info(clientEmail);
    }
  }, [clientEmail, message]);

  useEffect(() => {
    setReceiptPdfUrl((prev) => {
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setConfirmationPdfUrl((prev) => {
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setHotelVoucherPdfUrl((prev) => {
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setOrderSummaryPdfUrl((prev) => {
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setReceiptError(null);
    setConfirmationError(null);
    setHotelVoucherError(null);
    setOrderSummaryError(null);

    const manoPreview =
      Boolean(leadId) &&
      !linkedOrderId &&
      (isManoCruiseBooking || Boolean(manoQuoteId));
    if (!linkedOrderId && !manoPreview) return undefined;

    let cancelled = false;
    (async () => {
      if (manoPreview) {
        if (manoQuotesLoading && !resolvedManoQuoteId) {
          return;
        }
        await loadOrderSummaryPdf();
        if (!cancelled) setDocPreviewTab("order_summary");
        return;
      }
      // Auto-load previews when the linked LDS/hotel order is available.
      const receipt = await loadReceiptPdf();
      if (cancelled) return;
      const conf = await loadConfirmationPdf();
      if (cancelled) return;
      await loadHotelVoucherPdf();
      if (cancelled) return;
      await loadOrderSummaryPdf();
      if (cancelled) return;
      if (!receipt && conf) setDocPreviewTab("confirmation");
      else setDocPreviewTab("receipt");
    })();

    return () => {
      cancelled = true;
    };
  }, [
    linkedOrderId,
    leadId,
    isManoCruiseBooking,
    manoQuoteId,
    resolvedManoQuoteId,
    manoQuotesLoading,
  ]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when Mano quote id resolves

  useEffect(
    () => () => {
      setReceiptPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setConfirmationPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setHotelVoucherPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
      setOrderSummaryPdfUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return null;
      });
    },
    []
  );

  const downloadReceiptPdf = useCallback(async () => {
    try {
      let url = receiptPdfUrl;
      if (!url) {
        url = await loadReceiptPdf();
      }
      if (!url) return;
      if (String(url).startsWith("http") && !String(url).startsWith("blob:")) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `קבלה_יעד_שריג_${linkedBooking?.booking_ref || linkedOrderId || "order"}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      message.error("הורדת הקבלה נכשלה");
    }
  }, [receiptPdfUrl, loadReceiptPdf, linkedBooking?.booking_ref, linkedOrderId, message]);

  const openReceiptPdf = useCallback(async () => {
    try {
      let url = receiptPdfUrl;
      if (!url) {
        url = await loadReceiptPdf();
      }
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      message.error("פתיחת הקבלה נכשלה");
    }
  }, [receiptPdfUrl, loadReceiptPdf, message]);

  const downloadBookingConfirmation = useCallback(async () => {
    if (!linkedOrderId) return;
    try {
      let url = confirmationPdfUrl;
      if (!url) {
        url = await loadConfirmationPdf();
      }
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `אישור_הזמנה_${linkedBooking?.booking_ref || linkedOrderId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      message.error("הורדת אישור ההזמנה נכשלה");
    }
  }, [
    confirmationPdfUrl,
    loadConfirmationPdf,
    linkedOrderId,
    linkedBooking?.booking_ref,
    message,
  ]);

  const downloadHotelVoucher = useCallback(async () => {
    if (!linkedOrderId) return;
    try {
      let url = hotelVoucherPdfUrl;
      if (!url) {
        url = await loadHotelVoucherPdf();
      }
      if (!url) {
        message.warning(hotelVoucherError || "שובר מלון לא זמין להזמנה זו");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `שובר_מלון_${linkedBooking?.booking_ref || linkedOrderId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      message.error("הורדת שובר המלון נכשלה");
    }
  }, [
    hotelVoucherPdfUrl,
    hotelVoucherError,
    loadHotelVoucherPdf,
    linkedOrderId,
    linkedBooking?.booking_ref,
    message,
  ]);

  const appendNoteLocal = useCallback((note) => {
    if (!note) return;
    setNotes((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (note.id && list.some((n) => String(n?.id) === String(note.id))) {
        return list;
      }
      return [...list, note];
    });
  }, []);

  const openOrderSummaryModal = useCallback(async () => {
    setOrderSummarySendEmail(
      String(clientEmail || displayEmail || emailRows?.[0] || "").trim()
    );
    setOrderSummaryModalOpen(true);
    setOrderSummaryFields(buildDefaultOrderSummaryFields(lead));
    try {
      const fields = await resolveManoOrderSummaryFields();
      setOrderSummaryFields(fields);
    } catch (err) {
      console.warn("[CrmLeadDetail] order-summary itinerary prefill failed", err);
    }
  }, [
    lead,
    clientEmail,
    displayEmail,
    emailRows,
    resolveManoOrderSummaryFields,
  ]);

  const updateOrderSummaryField = useCallback((key, value) => {
    setOrderSummaryFields((prev) => {
      const next = { ...(prev || {}), [key]: value };
      // Editing free-text itinerary clears structured days so PDF uses the typed text.
      if (key === "itinerary_text") {
        next.itinerary = [];
      }
      return next;
    });
  }, []);

  const runManualOrderSummary = useCallback(
    async (mode) => {
      if (!leadId) {
        message.warning("חסר מזהה ליד");
        return;
      }
      const fields = { ...(orderSummaryFields || {}) };
      const skipKeys = new Set([
        "legal_text",
        "terms_url",
        "itinerary",
        "sailing_id",
        "rooms",
        "mano_quote_id",
      ]);
      const hasAny = Object.entries(fields).some(([key, value]) => {
        if (skipKeys.has(key)) return false;
        if (Array.isArray(value)) return value.length > 0;
        return Boolean(String(value || "").trim());
      });
      if (!hasAny) {
        message.warning("מלאו לפחות שדה אחד בסיכום ההזמנה");
        return;
      }
      if (mode === "send") {
        const email = String(orderSummarySendEmail || "").trim();
        if (!email || !email.includes("@")) {
          message.warning("יש להזין כתובת מייל תקינה לשליחה");
          return;
        }
      }
      setOrderSummaryBusy(mode);
      try {
        if (mode === "send") {
          await sendAgentLeadOrderSummary(leadId, {
            toEmail: String(orderSummarySendEmail || "").trim(),
            fields,
          });
          message.success("סיכום ההזמנה נשלח ללקוח");
          try {
            const res = await createAgentPublicLeadNote(leadId, {
              note_text: [
                "שליחת סיכום הזמנה מה-CRM (ידני)",
                `זמן: ${new Date().toLocaleString("he-IL")}`,
                `נמען: ${String(orderSummarySendEmail || "").trim()}`,
                fields.booking_ref
                  ? `מספר הזמנה: ${fields.booking_ref}`
                  : null,
              ]
                .filter(Boolean)
                .join("\n"),
            });
            if (res?.note) appendNoteLocal(res.note);
          } catch {
            /* non-blocking */
          }
          setOrderSummaryModalOpen(false);
          return;
        }
        const blob = await fetchAgentLeadOrderSummaryPdf(leadId, fields);
        const url = URL.createObjectURL(blob);
        if (mode === "preview") {
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        }
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `סיכום_הזמנה_${fields.booking_ref || leadId}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (err) {
        console.error(err);
        message.error(
          err?.message && String(err.message).length < 160
            ? String(err.message)
            : mode === "send"
              ? "שליחת סיכום ההזמנה נכשלה"
              : "יצירת סיכום ההזמנה נכשלה"
        );
      } finally {
        setOrderSummaryBusy(null);
      }
    },
    [
      appendNoteLocal,
      leadId,
      message,
      orderSummaryFields,
      orderSummarySendEmail,
    ]
  );

  const downloadOrderSummary = useCallback(async () => {
    const canAuto =
      Boolean(linkedOrderId) ||
      (Boolean(leadId) && (isManoCruiseBooking || Boolean(manoQuoteId)));
    if (!canAuto) {
      openOrderSummaryModal();
      return;
    }
    try {
      const manoAutoSummary =
        Boolean(leadId) && (isManoCruiseBooking || Boolean(manoQuoteId));
      let url = manoAutoSummary ? null : orderSummaryPdfUrl;
      if (!url) {
        url = await loadOrderSummaryPdf();
      }
      if (!url) {
        message.warning(orderSummaryError || "סיכום הזמנה לא זמין להזמנה זו");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `סיכום_הזמנה_${
          linkedBooking?.booking_ref || linkedOrderId || manoQuoteId || leadId
        }.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      message.error("הורדת סיכום ההזמנה נכשלה");
    }
  }, [
    orderSummaryPdfUrl,
    orderSummaryError,
    loadOrderSummaryPdf,
    linkedOrderId,
    linkedBooking?.booking_ref,
    message,
    openOrderSummaryModal,
    leadId,
    isManoCruiseBooking,
    manoQuoteId,
  ]);

  const reloadNotes = useCallback(async () => {
    if (!leadId) return;
    try {
      const res = await fetchAgentPublicLeadNotes(leadId, { limit: 250 });
      if (!res?.success) return;
      const apiNotes = Array.isArray(res?.notes) ? res.notes : [];
      const seed = lead || initialLeadRef.current;
      setNotes(mergeLeadNotes(apiNotes, seed, leadId));
    } catch (err) {
      console.warn("[CrmLeadDetail] reload notes failed", err);
    }
  }, [lead, leadId]);

  const revokeExtraPreviewUrl = useCallback(() => {
    const prev = extraFilePreviewUrlRef.current;
    if (prev && String(prev).startsWith("blob:")) {
      URL.revokeObjectURL(prev);
    }
    extraFilePreviewUrlRef.current = null;
    setExtraFilePreviewUrl(null);
  }, []);

  const reloadExtraFiles = useCallback(async () => {
    if (!leadId) return;
    setExtraFilesLoading(true);
    try {
      const res = await fetchAgentPublicLeadFiles(leadId, { limit: 50 });
      if (!res?.success) return;
      const files = Array.isArray(res.files) ? res.files : [];
      setExtraFiles(files);
      setSelectedExtraFileId((cur) => {
        if (cur && files.some((f) => String(f.id) === String(cur))) return cur;
        return files[0]?.id ? String(files[0].id) : null;
      });
    } catch (err) {
      console.warn("[CrmLeadDetail] reload extra files failed", err);
    } finally {
      setExtraFilesLoading(false);
    }
  }, [leadId]);

  const reloadPaymentEntries = useCallback(async () => {
    if (!leadId) return;
    setPaymentEntriesLoading(true);
    try {
      const res = await fetchAgentPublicLeadPaymentEntries(leadId, { limit: 100 });
      if (!res?.success) return;
      setPaymentEntries(Array.isArray(res.entries) ? res.entries : []);
    } catch (err) {
      console.warn("[CrmLeadDetail] reload payment entries failed", err);
    } finally {
      setPaymentEntriesLoading(false);
    }
  }, [leadId]);

  const openPaymentAddModal = useCallback(() => {
    if (!canManagePaymentEntries) {
      message.warning("רק אדמין יכול להוסיף תשלומים בדוקט");
      return;
    }
    setPaymentFormMethod("bank_transfer");
    setPaymentFormDirection("credit");
    setPaymentFormAmount("");
    setPaymentFormNote("");
    setPaymentFormDate("");
    setPaymentAddOpen(true);
  }, [canManagePaymentEntries, message]);

  const submitPaymentEntry = useCallback(async () => {
    if (!leadId) return;
    if (!canManagePaymentEntries) {
      message.warning("רק אדמין יכול להוסיף תשלומים בדוקט");
      return;
    }
    const raw = String(paymentFormAmount || "").replace(/,/g, "").trim();
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount === 0) {
      message.warning("יש להזין סכום");
      return;
    }
    setPaymentEntrySaving(true);
    try {
      const res = await createAgentPublicLeadPaymentEntry(leadId, {
        method: paymentFormMethod,
        amount_ils: Math.abs(amount),
        direction:
          amount < 0 || paymentFormDirection === "debit" ? "debit" : "credit",
        note: String(paymentFormNote || "").trim() || undefined,
        entry_date: String(paymentFormDate || "").trim() || undefined,
        order_id: linkedOrderId || undefined,
        entry_kind: "manual",
      });
      if (res?.note) appendNoteLocal(res.note);
      await reloadPaymentEntries();
      setPaymentAddOpen(false);
      message.success("התשלום נרשם בדוקט");
    } catch (err) {
      message.error(err?.message || "שגיאה בהוספת תשלום");
    } finally {
      setPaymentEntrySaving(false);
    }
  }, [
    appendNoteLocal,
    canManagePaymentEntries,
    leadId,
    linkedOrderId,
    message,
    paymentFormAmount,
    paymentFormDate,
    paymentFormDirection,
    paymentFormMethod,
    paymentFormNote,
    reloadPaymentEntries,
  ]);

  const cancelPaymentReceivedRow = useCallback(
    async (row) => {
      if (!leadId || !row) return;
      if (!canManagePaymentEntries) {
        message.warning("רק אדמין יכול לבטל או להסתיר תשלומים בדוקט");
        return;
      }
      const busyKey = String(row.key || row.entry_id || "");
      setPaymentRowBusyKey(busyKey);
      try {
        if (row.cancel_mode === "hide_auto") {
          const res = await createAgentPublicLeadPaymentEntry(leadId, {
            entry_kind: "hide_auto",
            hides_auto_key: String(row.key || "").trim(),
            method: row.method || "other",
            amount_ils: Math.abs(Number(row.amount_ils) || 0) || 0.01,
            direction: "credit",
            order_id: linkedOrderId || undefined,
            note: `הסתרת שורת הזמנה: ${row.method_label || row.method || ""}`,
          });
          if (res?.note) appendNoteLocal(res.note);
          message.success("שורת ההזמנה הוסתרה בדוקט");
        } else if (row.entry_id) {
          const res = await cancelAgentPublicLeadPaymentEntry(
            leadId,
            row.entry_id
          );
          if (res?.note) appendNoteLocal(res.note);
          message.success("התשלום בוטל בדוקט");
        }
        await reloadPaymentEntries();
      } catch (err) {
        message.error(err?.message || "שגיאה בביטול השורה");
      } finally {
        setPaymentRowBusyKey(null);
      }
    },
    [
      appendNoteLocal,
      canManagePaymentEntries,
      leadId,
      linkedOrderId,
      message,
      reloadPaymentEntries,
    ]
  );

  const loadExtraFilePreview = useCallback(
    async (fileId) => {
      if (!leadId || !fileId) return null;
      setExtraFilePreviewLoading(true);
      try {
        const url = await fetchAgentPublicLeadFileBlobUrl(leadId, fileId);
        revokeExtraPreviewUrl();
        extraFilePreviewUrlRef.current = url;
        setExtraFilePreviewUrl(url);
        return url;
      } catch (err) {
        console.error(err);
        message.error("טעינת הקובץ הנוסף נכשלה");
        return null;
      } finally {
        setExtraFilePreviewLoading(false);
      }
    },
    [leadId, message, revokeExtraPreviewUrl]
  );

  const uploadExtraPdfFiles = useCallback(
    async (fileList) => {
      if (!canUploadExtraFiles) {
        message.warning("רק אדמין יכול להעלות קבצים נוספים בשלב זה");
        return;
      }
      if (!leadId) {
        message.warning("אין תיק פתוח להעלאת קובץ");
        return;
      }
      const files = Array.from(fileList || []).filter(Boolean);
      const pdfs = files.filter((f) => {
        const name = String(f.name || "").toLowerCase();
        const type = String(f.type || "").toLowerCase();
        return name.endsWith(".pdf") || type === "application/pdf";
      });
      if (!pdfs.length) {
        message.warning("ניתן להעלות קבצי PDF בלבד");
        return;
      }
      setExtraFileUploading(true);
      try {
        let lastFile = null;
        for (const file of pdfs) {
          const res = await uploadAgentPublicLeadFile(leadId, file, file.name);
          if (res?.note) appendNoteLocal(res.note);
          if (res?.file) lastFile = res.file;
        }
        await reloadExtraFiles();
        if (lastFile?.id) {
          setSelectedExtraFileId(String(lastFile.id));
          setDocPreviewTab("extra");
          await loadExtraFilePreview(String(lastFile.id));
        }
        message.success(
          pdfs.length === 1 ? "הקובץ הועלה בהצלחה" : `${pdfs.length} קבצים הועלו בהצלחה`
        );
      } catch (err) {
        console.error(err);
        message.error(err?.message || "העלאת הקובץ נכשלה");
      } finally {
        setExtraFileUploading(false);
      }
    },
    [
      appendNoteLocal,
      canUploadExtraFiles,
      leadId,
      loadExtraFilePreview,
      message,
      reloadExtraFiles,
    ]
  );

  const onComposerDragOver = useCallback(
    (e) => {
      if (!canUploadExtraFiles) return;
      e.preventDefault();
      e.stopPropagation();
      setComposerDragOver(true);
    },
    [canUploadExtraFiles]
  );

  const onComposerDragLeave = useCallback(
    (e) => {
      if (!canUploadExtraFiles) return;
      e.preventDefault();
      e.stopPropagation();
      setComposerDragOver(false);
    },
    [canUploadExtraFiles]
  );

  const onComposerDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setComposerDragOver(false);
      if (!canUploadExtraFiles) {
        message.warning("רק אדמין יכול להעלות קבצים נוספים בשלב זה");
        return;
      }
      const dropped = e.dataTransfer?.files;
      if (dropped?.length) void uploadExtraPdfFiles(dropped);
    },
    [canUploadExtraFiles, message, uploadExtraPdfFiles]
  );

  useEffect(() => {
    if (!leadId) return undefined;
    const onFocusOrVisible = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      void reloadNotes();
    };
    const onOfferNoted = (ev) => {
      if (String(ev?.detail?.leadId || "") === String(leadId)) {
        void reloadNotes();
      }
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    window.addEventListener(CRM_LEAD_OFFER_NOTED_EVENT, onOfferNoted);
    return () => {
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
      window.removeEventListener(CRM_LEAD_OFFER_NOTED_EVENT, onOfferNoted);
    };
  }, [leadId, reloadNotes]);

  const openSendFilesModal = useCallback(() => {
    const defaults = ["confirmation", "receipt", "hotel_voucher", "order_summary"];
    const extraIds = (extraFiles || [])
      .map((f) => (f?.id ? `extra:${f.id}` : null))
      .filter(Boolean);
    setSendFilesSelection([...defaults, ...extraIds]);
    setSendFilesModalOpen(true);
  }, [extraFiles]);

  const sendDocuments = useCallback(
    async (modeOrSelection) => {
      const emails = [
        ...new Set(
          (emailRows || [])
            .map((e) => String(e || "").trim())
            .filter((e) => e && e.includes("@"))
        ),
      ];
      if (!emails.length) {
        message.warning("יש להזין לפחות כתובת מייל תקינה אחת");
        return;
      }

      let documents = [];
      let selectedExtraIds = [];
      let modeLabel = "";
      const DOC_KEYS = new Set([
        "confirmation",
        "receipt",
        "hotel_voucher",
        "order_summary",
      ]);

      if (typeof modeOrSelection === "string") {
        if (!linkedOrderId) {
          message.warning("חסרה הזמנה מקושרת לשליחת מסמך");
          return;
        }
        const mode = modeOrSelection;
        documents =
          mode === "both" || mode === "bundle"
            ? ["confirmation", "receipt", "hotel_voucher", "order_summary"]
            : mode === "receipt"
              ? ["receipt"]
              : mode === "hotel_voucher"
                ? ["hotel_voucher"]
                : mode === "order_summary"
                  ? ["order_summary"]
                  : ["confirmation"];
        modeLabel =
          mode === "both" || mode === "bundle"
            ? "אישור הזמנה + קבלה מיעד שריג + שובר מלון + סיכום הזמנה"
            : mode === "receipt"
              ? "קבלה מיעד שריג"
              : mode === "hotel_voucher"
                ? "שובר מלון"
                : mode === "order_summary"
                  ? "סיכום הזמנה"
                  : "אישור הזמנה";
        setSendSaving(mode === "both" ? "bundle" : mode);
      } else {
        const selected = Array.isArray(modeOrSelection?.keys)
          ? modeOrSelection.keys
          : Array.isArray(modeOrSelection)
            ? modeOrSelection
            : [];
        documents = selected.filter((k) => DOC_KEYS.has(k));
        selectedExtraIds = selected
          .filter((k) => String(k).startsWith("extra:"))
          .map((k) => String(k).slice("extra:".length))
          .filter(Boolean);
        if (!documents.length && !selectedExtraIds.length) {
          message.warning("יש לבחור לפחות קובץ אחד לשליחה");
          return;
        }
        if (documents.length && !linkedOrderId) {
          message.warning("לא ניתן לשלוח מסמכי הזמנה — חסרה הזמנה מקושרת");
          return;
        }
        if (selectedExtraIds.length && !leadId) {
          message.warning("לא ניתן לשלוח קבצים נוספים — חסר מזהה תיק");
          return;
        }
        const labels = [];
        if (documents.includes("confirmation")) labels.push("אישור הזמנה");
        if (documents.includes("receipt")) labels.push("קבלה מיעד שריג");
        if (documents.includes("hotel_voucher")) labels.push("שובר מלון");
        if (documents.includes("order_summary")) labels.push("סיכום הזמנה");
        for (const fid of selectedExtraIds) {
          const meta = extraFiles.find((f) => String(f.id) === String(fid));
          labels.push(meta?.original_filename || "קובץ נוסף");
        }
        modeLabel = labels.join(" + ");
        setSendSaving("bundle");
        setSendFilesModalOpen(false);
      }

      const statusHe = (docResult) => {
        const st = String(docResult?.status || "").toLowerCase();
        if (st === "success" || st === "ok") return "נשלח";
        if (st === "skipped") return "דולג (כבר נשלח)";
        if (st === "error") return "נכשל";
        return st || "לא בוצע";
      };

      const logSendAction = async (lines) => {
        const docLeadId = leadId || lead?.id;
        if (!docLeadId) {
          message.warning("השליחה בוצעה, אך לא ניתן לתעד — חסר מזהה ליד");
          return;
        }
        try {
          const res = await createAgentPublicLeadNote(docLeadId, {
            note_text: lines.filter(Boolean).join("\n"),
          });
          if (!res?.success || !res?.note) {
            throw new Error(
              res?.message || res?.error?.message || "note_create_failed"
            );
          }
          appendNoteLocal(res.note);
          setLeftPaneMode(LEFT_PANE_ACTIONS);
          await reloadNotes();
        } catch (err) {
          console.warn("[CrmLeadDetail] send docs note failed", err);
          message.warning(
            "השליחה בוצעה, אך רישום הפעולה נכשל — נסו לרענן את התיק"
          );
        }
      };

      try {
        const perEmail = [];
        let lastErr = null;

        for (const email of emails) {
          const row = { email, data: {}, extra: null, ok: true, error: null };
          if (documents.length) {
            try {
              const data = await sendCrmOrderDocuments(linkedOrderId, {
                toEmail: email,
                documents,
              });
              row.data = data || {};
            } catch (err) {
              lastErr = err;
              row.ok = false;
              row.error = err;
              row.data = err?.payload || {};
            }
          }
          if (selectedExtraIds.length) {
            try {
              const extraRes = await sendAgentPublicLeadFiles(leadId, {
                toEmails: [email],
                fileIds: selectedExtraIds,
                orderId: linkedOrderId,
              });
              row.extra = extraRes;
              const st = String(extraRes?.status || "").toLowerCase();
              if (st === "error") {
                row.ok = false;
                lastErr = lastErr || new Error(extraRes?.error || "extra_send_failed");
              }
            } catch (err) {
              lastErr = err;
              row.ok = false;
              row.error = err;
              row.extra = err?.payload || null;
            }
          }
          perEmail.push(row);
        }

        const noteLines = ["שליחת מסמכים מה-CRM"];
        noteLines.push(`זמן: ${new Date().toLocaleString("he-IL")}`);
        noteLines.push(`מסמכים שנבחרו: ${modeLabel}`);

        let confOkCount = 0;
        let confFailCount = 0;
        let receiptOkCount = 0;
        let receiptFailCount = 0;
        let hotelVoucherOkCount = 0;
        let hotelVoucherFailCount = 0;
        let orderSummaryOkCount = 0;
        let orderSummaryFailCount = 0;
        let extraOkCount = 0;
        let extraFailCount = 0;
        let lastConfErr = "";
        let lastReceiptErr = "";
        let lastHotelVoucherErr = "";
        let lastOrderSummaryErr = "";

        for (const row of perEmail) {
          const data = row.data || {};
          noteLines.push("");
          noteLines.push(`נמען: ${row.email}`);
          if (documents.includes("confirmation")) {
            const conf = data.confirmation;
            const label = row.data && !row.error ? statusHe(conf) : "נכשל";
            noteLines.push(`• אישור הזמנה: ${label}`);
            const err =
              conf?.error ||
              (!row.ok && !conf && documents.length ? row.error?.message : null) ||
              "";
            if (err) {
              noteLines.push(`  פירוט: ${String(err).slice(0, 240)}`);
              lastConfErr = String(err);
              confFailCount += 1;
            } else if ((conf?.status || "").toLowerCase() === "error") {
              confFailCount += 1;
              lastConfErr = String(conf?.error || "error");
            } else if (
              ["success", "ok", "skipped"].includes(
                String(conf?.status || "").toLowerCase()
              )
            ) {
              confOkCount += 1;
            } else if (!row.ok && documents.includes("confirmation")) {
              confFailCount += 1;
            }
          }
          if (documents.includes("receipt")) {
            const receipt = data.receipt;
            const label = statusHe(receipt);
            noteLines.push(`• קבלה מיעד שריג: ${label}`);
            if (receipt?.trans_id) {
              noteLines.push(`  TransId: ${receipt.trans_id}`);
            }
            const err =
              receipt?.error ||
              (!row.ok && !receipt && documents.includes("receipt")
                ? row.error?.message
                : null) ||
              "";
            if (err) {
              noteLines.push(`  פירוט: ${String(err).slice(0, 240)}`);
              lastReceiptErr = String(err);
              receiptFailCount += 1;
            } else if ((receipt?.status || "").toLowerCase() === "error") {
              receiptFailCount += 1;
              lastReceiptErr = String(receipt?.error || "error");
            } else if (
              ["success", "ok"].includes(String(receipt?.status || "").toLowerCase())
            ) {
              receiptOkCount += 1;
            } else if (!row.ok) {
              receiptFailCount += 1;
            }
          }
          if (documents.includes("hotel_voucher")) {
            const hv = data.hotel_voucher;
            const label = statusHe(hv);
            noteLines.push(`• שובר מלון: ${label}`);
            const err =
              hv?.error ||
              (!row.ok && !hv && documents.includes("hotel_voucher")
                ? row.error?.message
                : null) ||
              "";
            if (err) {
              noteLines.push(`  פירוט: ${String(err).slice(0, 240)}`);
              lastHotelVoucherErr = String(err);
              hotelVoucherFailCount += 1;
            } else if ((hv?.status || "").toLowerCase() === "error") {
              hotelVoucherFailCount += 1;
              lastHotelVoucherErr = String(hv?.error || "error");
            } else if (
              ["success", "ok"].includes(String(hv?.status || "").toLowerCase())
            ) {
              hotelVoucherOkCount += 1;
            } else if (!row.ok) {
              hotelVoucherFailCount += 1;
            }
          }
          if (documents.includes("order_summary")) {
            const os = data.order_summary;
            const label = statusHe(os);
            noteLines.push(`• סיכום הזמנה: ${label}`);
            const err =
              os?.error ||
              (!row.ok && !os && documents.includes("order_summary")
                ? row.error?.message
                : null) ||
              "";
            if (err) {
              noteLines.push(`  פירוט: ${String(err).slice(0, 240)}`);
              lastOrderSummaryErr = String(err);
              orderSummaryFailCount += 1;
            } else if ((os?.status || "").toLowerCase() === "error") {
              orderSummaryFailCount += 1;
              lastOrderSummaryErr = String(os?.error || "error");
            } else if (
              ["success", "ok"].includes(String(os?.status || "").toLowerCase())
            ) {
              orderSummaryOkCount += 1;
            } else if (!row.ok) {
              orderSummaryFailCount += 1;
            }
          }
          if (selectedExtraIds.length) {
            const results = Array.isArray(row.extra?.results)
              ? row.extra.results
              : [];
            const fileResults =
              results.find((r) => r.email === row.email)?.result?.files ||
              row.extra?.files ||
              [];
            if (Array.isArray(fileResults) && fileResults.length) {
              for (const fr of fileResults) {
                const fname = fr.original_filename || fr.file_id || "קובץ";
                const st = String(fr.status || "").toLowerCase();
                noteLines.push(
                  `• קובץ נוסף (${fname}): ${st === "success" || st === "ok" ? "נשלח" : "נכשל"}`
                );
                if (st === "success" || st === "ok") extraOkCount += 1;
                else {
                  extraFailCount += 1;
                  if (fr.error) noteLines.push(`  פירוט: ${String(fr.error).slice(0, 240)}`);
                }
              }
            } else if (row.extra?.status === "success") {
              noteLines.push(`• קבצים נוספים: נשלחו`);
              extraOkCount += selectedExtraIds.length;
            } else {
              noteLines.push(`• קבצים נוספים: נכשל`);
              extraFailCount += 1;
              const err = row.extra?.error || row.error?.message;
              if (err) noteLines.push(`  פירוט: ${String(err).slice(0, 240)}`);
            }
          }
        }

        await logSendAction(noteLines);

        const wantsConf = documents.includes("confirmation");
        const wantsReceipt = documents.includes("receipt");
        const wantsHotelVoucher = documents.includes("hotel_voucher");
        const wantsOrderSummary = documents.includes("order_summary");
        const wantsExtra = selectedExtraIds.length > 0;
        const confAllOk = !wantsConf || (confOkCount > 0 && confFailCount === 0);
        const receiptAllOk =
          !wantsReceipt || (receiptOkCount > 0 && receiptFailCount === 0);
        const hotelVoucherAllOk =
          !wantsHotelVoucher ||
          (hotelVoucherOkCount > 0 && hotelVoucherFailCount === 0);
        const orderSummaryAllOk =
          !wantsOrderSummary ||
          (orderSummaryOkCount > 0 && orderSummaryFailCount === 0);
        const extraAllOk = !wantsExtra || (extraOkCount > 0 && extraFailCount === 0);
        const confAnyOk = confOkCount > 0;
        const receiptAnyOk = receiptOkCount > 0;
        const hotelVoucherAnyOk = hotelVoucherOkCount > 0;
        const orderSummaryAnyOk = orderSummaryOkCount > 0;
        const extraAnyOk = extraOkCount > 0;

        if (
          !confAnyOk &&
          !receiptAnyOk &&
          !hotelVoucherAnyOk &&
          !orderSummaryAnyOk &&
          !extraAnyOk &&
          lastErr
        )
          throw lastErr;

        if (
          confAllOk &&
          receiptAllOk &&
          hotelVoucherAllOk &&
          orderSummaryAllOk &&
          extraAllOk
        ) {
          const suffix =
            emails.length > 1 ? ` (${emails.length} נמענים)` : "";
          message.success(`הקבצים נשלחו ללקוח${suffix}`);
          return;
        }

        const parts = [];
        if (wantsConf) {
          parts.push(
            confAnyOk && confFailCount === 0
              ? "אישור הזמנה נשלח"
              : confAnyOk
                ? "אישור הזמנה נשלח חלקית"
                : `אישור הזמנה נכשל${lastConfErr ? `: ${lastConfErr.slice(0, 80)}` : ""}`
          );
        }
        if (wantsReceipt) {
          parts.push(
            receiptAnyOk && receiptFailCount === 0
              ? "קבלה נשלחה"
              : receiptAnyOk
                ? "קבלה נשלחה חלקית"
                : `קבלה נכשלה${lastReceiptErr ? `: ${lastReceiptErr.slice(0, 80)}` : ""}`
          );
        }
        if (wantsHotelVoucher) {
          parts.push(
            hotelVoucherAnyOk && hotelVoucherFailCount === 0
              ? "שובר מלון נשלח"
              : hotelVoucherAnyOk
                ? "שובר מלון נשלח חלקית"
                : `שובר מלון נכשל${
                    lastHotelVoucherErr
                      ? `: ${lastHotelVoucherErr.slice(0, 80)}`
                      : ""
                  }`
          );
        }
        if (wantsOrderSummary) {
          parts.push(
            orderSummaryAnyOk && orderSummaryFailCount === 0
              ? "סיכום הזמנה נשלח"
              : orderSummaryAnyOk
                ? "סיכום הזמנה נשלח חלקית"
                : `סיכום הזמנה נכשל${
                    lastOrderSummaryErr
                      ? `: ${lastOrderSummaryErr.slice(0, 80)}`
                      : ""
                  }`
          );
        }
        if (wantsExtra) {
          parts.push(
            extraAnyOk && extraFailCount === 0
              ? "קבצים נוספים נשלחו"
              : extraAnyOk
                ? "קבצים נוספים נשלחו חלקית"
                : "קבצים נוספים נכשלו"
          );
        }
        message.warning(parts.join(" · "));
      } catch (err) {
        console.error(err);
        const detail =
          err?.payload?.receipt?.error ||
          err?.payload?.confirmation?.error ||
          err?.payload?.hotel_voucher?.error ||
          err?.payload?.order_summary?.error ||
          err?.message;
        const failNote = [
          "שליחת מסמכים מה-CRM — נכשלה",
          `זמן: ${new Date().toLocaleString("he-IL")}`,
          `נמענים: ${emails.join(", ")}`,
          `פירוט: ${String(detail || "send_failed").slice(0, 300)}`,
        ];
        await logSendAction(failNote);
        message.error(
          typeof detail === "string" && detail.length < 180
            ? `שליחה נכשלה: ${detail}`
            : "שליחת המסמכים נכשלה"
        );
      } finally {
        setSendSaving(null);
      }
    },
    [
      appendNoteLocal,
      emailRows,
      extraFiles,
      lead,
      leadId,
      linkedOrderId,
      message,
      reloadNotes,
    ]
  );

  const hydrateLinkedBooking = useCallback(async (leadRow) => {
    if (!leadRow) return leadRow;
    const manoCruise =
      isManoCruiseLinkedBooking(leadRow.linked_booking) ||
      parseManoQuoteIdFromLead(leadRow);
    if (manoCruise) {
      const cruiseOrderId = String(leadRow.linked_booking?.order_id || "").trim();
      if (!cruiseOrderId) return leadRow;
      try {
        const res = await getCruiseOrder({ id: cruiseOrderId });
        const order = res?.data?.data || res?.data || res;
        if (!order || typeof order !== "object" || order.id == null) return leadRow;
        return {
          ...leadRow,
          linked_booking: mergeCruiseOrderIntoLinkedBooking(
            leadRow.linked_booking || {},
            order
          ),
        };
      } catch (err) {
        console.warn("[CrmLeadDetail] cruise hydrate failed", err);
        return leadRow;
      }
    }
    const orderId =
      String(leadRow.linked_booking?.order_id || "").trim() ||
      extractOrderIdFromCrmLead(leadRow);
    if (!orderId) return leadRow;
    try {
      const res = await getLdsOrder({ id: orderId });
      const order = extractLdsOrderRowFromFetchResponse(res?.data ?? res);
      // Always rebuild from live order (same SSOT as order details payment card)
      const linked = buildLinkedBookingFromLdsOrder(order);
      if (!linked) return leadRow;
      const liveDestination = String(
        linked.interest_destination || linked.hotel_name || ""
      ).trim();
      const liveCategory = String(
        linked.deal_type_label || leadRow.category || ""
      ).trim();
      const liveAi = String(linked.ai_sales_note || "").trim();
      const liveMsg = String(linked.message || "").trim();
      const liveInternal = String(linked.agent_internal_note || "").trim();
      const liveAmount = String(linked.amount_display || "").trim();
      const staleInterest =
        !leadRow.interest_destination ||
        String(leadRow.interest_destination).trim() === "מלון" ||
        isStaleCrmLeadCopy(leadRow.interest_destination, liveAmount);
      const staleCategory =
        !leadRow.category ||
        String(leadRow.category).trim() === "הזמנה";
      const staleAi = isStaleCrmLeadCopy(leadRow.ai_sales_note, liveAmount);
      const staleInternal = isStaleCrmLeadCopy(
        leadRow.agent_internal_note,
        liveAmount
      );
      return {
        ...leadRow,
        linked_booking: linked,
        interest_destination:
          staleInterest && liveDestination
            ? liveDestination
            : leadRow.interest_destination || liveDestination || leadRow.interest_destination,
        category:
          staleCategory && liveCategory && liveCategory !== "LDS"
            ? liveCategory
            : leadRow.category || liveCategory || leadRow.category,
        ai_sales_note:
          staleAi && liveAi ? liveAi : leadRow.ai_sales_note || liveAi || leadRow.ai_sales_note,
        message:
          liveMsg && (staleAi || !leadRow.message)
            ? liveMsg
            : leadRow.message || liveMsg || leadRow.message,
        agent_internal_note:
          staleInternal && liveInternal
            ? liveInternal
            : leadRow.agent_internal_note || liveInternal || leadRow.agent_internal_note,
        source_type: leadRow.source_type || "completed_order",
      };
    } catch (err) {
      console.warn("[CrmLeadDetail] hydrate linked_booking failed", err);
      return leadRow;
    }
  }, []);

  // Re-sync financials from live order when returning to the tab (commission edits, post-order actions)
  useEffect(() => {
    if (!leadId) return undefined;
    const onFocus = () => {
      setLead((prev) => {
        if (!prev) return prev;
        void hydrateLinkedBooking(prev).then((hydrated) => {
          if (!hydrated?.linked_booking) return;
          setLead((cur) => mergeLeadPreserveBooking(cur, hydrated));
          onLeadUpdatedRef.current?.(hydrated);
        });
        return prev;
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hydrateLinkedBooking, leadId, mergeLeadPreserveBooking]);

  // Load once per leadId — then always rehydrate booking financials from live order SSOT.
  useEffect(() => {
    if (!leadId) return undefined;
    let cancelled = false;
    const seed = initialLeadRef.current;
    const hasSeed = Boolean(seed?.id) && String(seed.id) === String(leadId);

    async function run() {
      if (!hasSeed) setLoading(true);
      try {
        const res = await fetchAgentPublicLead(leadId, { includeBooking: true });
        if (cancelled) return;
        if (!res?.success || !res?.lead) {
          throw new Error(res?.error?.message || "ליד לא נמצא");
        }
        setLead((prev) => {
          const merged = mergeLeadPreserveBooking(prev || seed, res.lead);
          return merged;
        });
        onLeadUpdatedRef.current?.(
          mergeLeadPreserveBooking(seed, res.lead)
        );
        const hydrated = await hydrateLinkedBooking(
          mergeLeadPreserveBooking(seed, res.lead)
        );
        if (cancelled || !hydrated) return;
        setLead((prev) => mergeLeadPreserveBooking(prev, hydrated));
        if (hydrated?.linked_booking?.order_id) {
          onLeadUpdatedRef.current?.(hydrated);
        }
        // Refresh documentation seed from live order totals / destination
        setNotes((prev) => {
          const apiOnly = (Array.isArray(prev) ? prev : []).filter(
            (n) => !String(n?.id || "").startsWith("legacy-")
          );
          return mergeLeadNotes(apiOnly, hydrated, leadId);
        });
      } catch (e) {
        if (cancelled) return;
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          /timeout/i.test(String(e?.message || "")) ||
          /timeout/i.test(String(e?.response?.data?.message || ""));
        messageRef.current.error(
          isTimeout
            ? "טעינת התיק ארכה יותר מדי. מציגים את הנתונים שכבר יש — ניתן לרענן."
            : e?.response?.data?.message || e?.message || "שגיאה בטעינת ליד"
        );
        if (!hasSeed) {
          onCloseRef.current?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [hydrateLinkedBooking, leadId, mergeLeadPreserveBooking]);

  useEffect(() => {
    if (!leadId) return undefined;
    // Ops deep-link: don't block the chat thread on a slow documentation fetch.
    if (leftPaneMode === LEFT_PANE_OPS) return undefined;
    if (notesFetchedForLeadRef.current === String(leadId)) return undefined;
    let cancelled = false;

    async function runNotes() {
      setNotesLoading(true);
      try {
        const res = await fetchAgentPublicLeadNotes(leadId, { limit: 250 });
        if (cancelled) return;
        if (!res?.success) {
          throw new Error(res?.error?.message || "שגיאה בטעינת תיעוד");
        }
        const apiNotes = Array.isArray(res?.notes) ? res.notes : [];
        const seed = lead || initialLeadRef.current;
        setNotes(mergeLeadNotes(apiNotes, seed, leadId));
      } catch (e) {
        if (cancelled) return;
        const isTimeout =
          e?.code === "ECONNABORTED" || /timeout/i.test(String(e?.message || ""));
        messageRef.current.error(
          isTimeout
            ? "טעינת התיעוד ארכה יותר מדי. אפשר לשלוח הודעה חדשה או לנסות שוב."
            : e?.message || "שגיאה בטעינת תיעוד"
        );
        const seed = lead || initialLeadRef.current;
        setNotes(mergeLeadNotes([], seed, leadId));
      } finally {
        if (!cancelled) {
          notesFetchedForLeadRef.current = String(leadId);
          setNotesLoading(false);
        }
      }
    }

    void runNotes();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when docs/actions needed
  }, [leadId, leftPaneMode]);

  // After live order hydrate, refresh system documentation seed (amount / destination)
  useEffect(() => {
    if (!leadId || !lead?.linked_booking?.agent_internal_note) return;
    setNotes((prev) => {
      const apiOnly = (Array.isArray(prev) ? prev : []).filter(
        (n) => !String(n?.id || "").startsWith("legacy-")
      );
      return mergeLeadNotes(apiOnly, lead, leadId);
    });
  }, [
    leadId,
    lead?.linked_booking?.agent_internal_note,
    lead?.linked_booking?.amount_display,
  ]);

  useEffect(() => {
    if (!leadId) {
      setExtraFiles([]);
      setSelectedExtraFileId(null);
      revokeExtraPreviewUrl();
      return undefined;
    }
    void reloadExtraFiles();
    return undefined;
  }, [leadId, reloadExtraFiles, revokeExtraPreviewUrl]);

  useEffect(() => {
    if (!leadId) {
      setPaymentEntries([]);
      return undefined;
    }
    void reloadPaymentEntries();
    return undefined;
  }, [leadId, reloadPaymentEntries]);

  useEffect(() => {
    if (docPreviewTab !== "extra" || !selectedExtraFileId) return undefined;
    void loadExtraFilePreview(selectedExtraFileId);
    return undefined;
  }, [docPreviewTab, selectedExtraFileId, loadExtraFilePreview]);

  useEffect(() => {
    return () => {
      const prev = extraFilePreviewUrlRef.current;
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
    };
  }, []);

  const previewSegmentOptions = useMemo(() => {
    const opts = [
      { label: "הצג קבלה", value: "receipt" },
      { label: "הצג אישור הזמנה", value: "confirmation" },
      { label: "הצג סיכום הזמנה", value: "order_summary" },
      { label: "הצג שובר מלון", value: "hotel_voucher" },
      {
        label:
          extraFiles.length > 1
            ? `קבצים נוספים (${extraFiles.length})`
            : "קבצים נוספים",
        value: "extra",
      },
    ];
    return opts;
  }, [extraFiles.length]);

  const sendFilesCheckboxOptions = useMemo(() => {
    const opts = [
      { label: "אישור הזמנה", value: "confirmation" },
      { label: "סיכום הזמנה", value: "order_summary" },
      { label: "קבלה / חשבונית מיעד שריג", value: "receipt" },
      { label: "שובר מלון", value: "hotel_voucher" },
    ];
    for (const f of extraFiles || []) {
      if (!f?.id) continue;
      opts.push({
        label: f.original_filename || "קובץ PDF נוסף",
        value: `extra:${f.id}`,
      });
    }
    return opts;
  }, [extraFiles]);

  const selectedExtraFile = useMemo(
    () =>
      extraFiles.find((f) => String(f?.id) === String(selectedExtraFileId || "")) ||
      null,
    [extraFiles, selectedExtraFileId]
  );
  const addNote = useCallback(async () => {
    const text = String(newNoteText || "").trim();
    if (!text || !leadId) {
      message.warning("יש להזין תוכן הודעה");
      return;
    }
    setNotesSaving(true);
    try {
      const res = await createAgentPublicLeadNote(leadId, { note_text: text });
      if (!res?.success) throw new Error(res?.error?.message || "שגיאה בשליחה");
      if (res?.note) setNotes((prev) => [...prev, res.note]);
      setNewNoteText("");
    } catch (e) {
      message.error(e?.message || "שגיאת רשת");
    } finally {
      setNotesSaving(false);
    }
  }, [leadId, message, newNoteText]);

  const onStatusChange = useCallback(
    async (status) => {
      if (!leadId) return;
      setStatusSaving(true);
      try {
        const res = await patchAgentPublicLead(leadId, { status });
        if (!res?.success) throw new Error(res?.error?.message || "עדכון נכשל");
        const next = mergeLeadPreserveBooking(lead, res.lead || { ...lead, status });
        setLead(next);
        onLeadUpdated?.(next);
        window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
      } catch (e) {
        message.error(e?.message || "שגיאה בעדכון סטטוס");
      } finally {
        setStatusSaving(false);
      }
    },
    [lead, leadId, mergeLeadPreserveBooking, message, onLeadUpdated]
  );

  const beginContactEdit = useCallback(() => {
    setContactPhoneDraft(String(displayPhone || ""));
    setContactEmailDraft(String(displayEmail || ""));
    setContactEditing(true);
  }, [displayEmail, displayPhone]);

  const saveContact = useCallback(async () => {
    if (!leadId) return;
    setContactSaving(true);
    try {
      const orderId =
        String(lead?.linked_booking?.order_id || "").trim() ||
        extractOrderIdFromCrmLead(lead) ||
        "";
      const bookingRef = String(
        lead?.linked_booking?.booking_ref || ""
      ).trim();
      const payload = {
        phone: String(contactPhoneDraft || "").trim(),
        email: String(contactEmailDraft || "").trim(),
        ...(orderId ? { order_id: orderId } : {}),
        ...(bookingRef ? { booking_ref: bookingRef } : {}),
      };

      let res = await patchAgentPublicLead(leadId, payload);
      let nextLead = res?.lead;

      // If docket missing — recreate from linked paid order, then patch again
      if ((!res?.success || !nextLead) && orderId) {
        const ensured = await createAgentPublicLeadFromOrder(orderId);
        const ensuredLead = ensured?.lead;
        if (ensuredLead?.id) {
          const ensuredId = String(ensuredLead.id);
          res = await patchAgentPublicLead(ensuredId, payload);
          nextLead = res?.lead || {
            ...ensuredLead,
            phone: payload.phone,
            email: payload.email,
          };
          if (ensuredId !== String(leadId)) {
            setLead((prev) =>
              mergeLeadPreserveBooking(prev, { ...nextLead, id: ensuredId })
            );
            onLeadUpdated?.(
              mergeLeadPreserveBooking(lead, { ...nextLead, id: ensuredId })
            );
            message.success("נפתח דוקט מההזמנה ופרטי יצירת קשר נשמרו");
            setContactEditing(false);
            return;
          }
        }
      }

      if (!res?.success || !nextLead) {
        throw new Error(res?.message || res?.error?.message || "עדכון נכשל");
      }
      const next = mergeLeadPreserveBooking(lead, nextLead);
      setLead(next);
      onLeadUpdated?.(next);
      setContactEditing(false);
      message.success("פרטי יצירת קשר עודכנו");
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || "שגיאה בעדכון פרטים"
      );
    } finally {
      setContactSaving(false);
    }
  }, [
    contactEmailDraft,
    contactPhoneDraft,
    lead,
    leadId,
    message,
    mergeLeadPreserveBooking,
    onLeadUpdated,
  ]);

  const isDocket = isCrmDocketLead(lead);

  const onDelete = useCallback(async () => {
    if (!leadId) return;
    setDeleting(true);
    try {
      const res = await deleteAgentPublicLead(leadId);
      if (!res?.success) throw new Error(res?.error?.message || "מחיקה נכשלה");
      message.success(isDocket ? "הדוקט נמחק" : "הליד נמחק");
      window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
      onDeleted?.(leadId);
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "שגיאה במחיקה");
    } finally {
      setDeleting(false);
    }
  }, [isDocket, leadId, message, onClose, onDeleted]);

  const onCreateOffer = useCallback(async () => {
    if (!lead?.id) return;
    try {
      await createAgentPublicLeadNote(lead.id, {
        note_text: [
          "התחיל יצירת הצעה מה-CRM (תיק לקוח)",
          `נפתח חלון חיפוש · ${new Date().toLocaleString("he-IL")}`,
        ].join("\n"),
      });
    } catch {
      // continue
    }
    startCrmLeadOfferHandoff(navigate, lead);
  }, [lead, navigate]);

  const reloadManoQuotesForLead = useCallback(async () => {
    const name = String(lead?.full_name || "").trim();
    const phone = String(displayPhone || "").trim();
    const search = name || phone;
    const sourceQuoteId = parseManoQuoteIdFromLead(lead);
    if (!search && !sourceQuoteId) {
      setManoQuotesForLead([]);
      return;
    }
    setManoQuotesLoading(true);
    try {
      let items = [];
      if (search) {
        const res = await listManoQuotes({ search, limit: 20 });
        items = Array.isArray(res?.items) ? res.items : [];
      }
      if (sourceQuoteId) {
        const match = items.find((q) => Number(q?.id) === Number(sourceQuoteId));
        if (match) {
          items = [
            match,
            ...items.filter((q) => Number(q?.id) !== Number(sourceQuoteId)),
          ];
        } else {
          try {
            const one = await getManoQuote(sourceQuoteId);
            const detail = one?.quote || one;
            if (detail?.id != null) {
              items = [detail, ...items];
            }
          } catch {
            /* keep search results */
          }
        }
        // Closed Mano dockets should still surface the linked quote first.
        setManoQuotesForLead(items.slice(0, 5));
      } else {
        const openOnes = items.filter(
          (q) => q?.status !== "archived" && q?.status !== "closed"
        );
        setManoQuotesForLead(openOnes.length ? openOnes : items.slice(0, 5));
      }
    } catch (err) {
      console.warn("[CrmLeadDetail] mano quotes lookup failed", err);
      setManoQuotesForLead([]);
    } finally {
      setManoQuotesLoading(false);
    }
  }, [lead, displayPhone]);

  useEffect(() => {
    void reloadManoQuotesForLead();
  }, [reloadManoQuotesForLead]);

  const onCloseManoQuoteAsDocket = useCallback(
    (quoteRow) => {
      if (!quoteRow?.id || !leadId) return;
      Modal.confirm({
        title: "סגירת הצעת מנו → דוקט",
        content:
          "ההצעה תסומן כנסגרה, פרטיה ייכנסו לתיעוד ולפעולות בדוקט, והליד יהפוך לדוקט. להמשיך?",
        okText: "סגור כנסגרה",
        cancelText: "חזרה",
        direction: "rtl",
        onOk: async () => {
          setManoCloseBusyId(quoteRow.id);
          try {
            const res = await closeManoQuote(quoteRow.id, { crmLeadId: leadId });
            const crm = res?.crm || {};
            message.success(
              crm.converted_to_docket
                ? "ההצעה נסגרה והליד הומר לדוקט"
                : "ההצעה נסגרה ופרטיה נרשמו בתיק"
            );
            try {
              const refreshed = await fetchAgentPublicLead(leadId);
              if (refreshed?.success && refreshed?.lead) {
                const next = mergeLeadPreserveBooking(lead, refreshed.lead);
                setLead(next);
                onLeadUpdated?.(next);
              }
            } catch {
              /* ignore */
            }
            await reloadNotes();
            await reloadManoQuotesForLead();
            setLeftPaneMode(LEFT_PANE_ACTIONS);
            window.dispatchEvent(new CustomEvent("publicLeadsStatusChanged"));
          } catch (err) {
            message.error(
              extractManoApiError(err, "סגירת הצעת מנו נכשלה")
            );
          } finally {
            setManoCloseBusyId(null);
          }
        },
      });
    },
    [
      lead,
      leadId,
      mergeLeadPreserveBooking,
      message,
      onLeadUpdated,
      reloadManoQuotesForLead,
      reloadNotes,
    ]
  );

  const onPayManoQuote = useCallback(
    (quoteRow) => {
      if (!quoteRow?.id) return;
      const alreadyClosed =
        quoteRow.status === "closed" || quoteRow.effective_status === "closed";
      const run = async () => {
        setManoPayBusyId(quoteRow.id);
        try {
          const res = await prepareManoQuotePayment(quoteRow.id, {
            crmLeadId: leadId,
          });
          const orderId =
            res?.cruise_order_id ||
            res?.crm?.cruise_order_id ||
            res?.crm?.cruise_order?.cruise_order_id;
          if (!orderId) {
            message.error("לא התקבל מספר הזמנת מנו לתשלום");
            return;
          }
          message.success("נפתח מסך תשלום מנו עם פרטי ההפלגה");
          navigate(
            buildManoCruisePaymentPath({
              orderId,
              quoteId: quoteRow.id,
            })
          );
        } catch (err) {
          message.error(extractManoApiError(err, "פתיחת תשלום מנו נכשלה"));
        } finally {
          setManoPayBusyId(null);
        }
      };
      if (alreadyClosed) {
        void run();
        return;
      }
      Modal.confirm({
        title: "תשלום להצעת מנו",
        content:
          "ייפתח מסך התשלום של מנו ספנות עם פרטי ההפלגה מההצעה. ההצעה תסומן כנסגרה, הדוקט יעודכן, ואחרי התשלום פרטי התשלום יישמרו בדוקט כמו בהזמנה רגילה.",
        okText: "מעבר לתשלום",
        cancelText: "חזרה",
        direction: "rtl",
        onOk: run,
      });
    },
    [leadId, message, navigate]
  );

  const isCrmMobile = useCrmMobileLayout();

  const leftPaneSegmentOptions = useMemo(() => {
    const opts = [
      { label: "תיעוד", value: LEFT_PANE_DOCS },
      {
        label: isCrmMobile ? "פעולות" : "פעולות בדוקט",
        value: LEFT_PANE_ACTIONS,
      },
      {
        label: isCrmMobile ? "צ׳אט" : "צ׳אט אופרציה",
        value: LEFT_PANE_OPS,
      },
    ];
    if (SHOW_CRM_WHATSAPP_PANE) {
      opts.push({ label: isCrmMobile ? "WhatsApp" : "שיחת WhatsApp", value: LEFT_PANE_WHATSAPP });
    }
    if (isCrmMobile) {
      opts.push({ label: "סיכום", value: LEFT_PANE_SUMMARY });
    }
    return opts;
  }, [isCrmMobile]);

  useEffect(() => {
    if (SHOW_CRM_WHATSAPP_PANE) return;
    if (leftPaneMode === LEFT_PANE_WHATSAPP) {
      setLeftPaneMode(LEFT_PANE_DOCS);
    }
  }, [leftPaneMode]);

  useEffect(() => {
    if (isCrmMobile) return;
    if (leftPaneMode === LEFT_PANE_SUMMARY) {
      setLeftPaneMode(LEFT_PANE_DOCS);
    }
  }, [isCrmMobile, leftPaneMode]);

  useEffect(() => {
    setLeftPaneMode(resolveLeftPaneMode(initialLeftPane, initialOpsRequestId));
  }, [leadId, initialLeftPane, initialOpsRequestId]);

  const whatsappNotes = useMemo(
    () =>
      (notes || []).filter((n) => {
        const t = String(n?.note_text || "");
        return (
          t.includes("וואטסאפ") ||
          t.includes("WhatsApp") ||
          t.startsWith("WA:") ||
          t.startsWith("שיחת WhatsApp")
        );
      }),
    [notes]
  );

  /** Human correspondence only — system / CRM action logs live under פעולות בדוקט. */
  const correspondenceNotes = useMemo(
    () => (notes || []).filter((n) => !isCrmActionAuditNote(n)),
    [notes]
  );

  const actionAuditNotes = useMemo(
    () => (notes || []).filter((n) => isCrmActionAuditNote(n)),
    [notes]
  );

  const openWhatsApp = useCallback(async () => {
    const phoneRaw = lead?.phone || lead?.linked_booking?.phone || "";
    const digits = sanitizeWhatsAppPhone(phoneRaw);
    if (!digits) {
      message.warning("אין מספר טלפון תקין לפתיחת WhatsApp");
      return;
    }
    if (SHOW_CRM_WHATSAPP_PANE) {
      setLeftPaneMode(LEFT_PANE_WHATSAPP);
    }

    const name = String(lead?.full_name || "").trim();
    const hotel = String(
      lead?.linked_booking?.hotel_name || lead?.interest_destination || ""
    ).trim();
    const ref = String(
      lead?.linked_booking?.booking_ref || lead?.linked_booking?.order_id || ""
    ).trim();
    const prefLines = [];
    if (name) prefLines.push(`שלום ${name},`);
    else prefLines.push("שלום,");
    prefLines.push("");
    prefLines.push("פונים אליך לגבי ההזמנה");
    if (hotel && hotel !== "מלון") prefLines.push(`מלון: ${hotel}`);
    if (ref) prefLines.push(`מס׳ הזמנה: ${ref}`);
    const prefText = prefLines.join("\n").trim();
    const url = buildWhatsAppChatUrl(digits, prefText);
    if (!url) {
      message.warning("לא ניתן לפתוח WhatsApp");
      return;
    }
    // Named window — reuse as the WhatsApp chat window (not the CRM docs pane)
    window.open(url, "hotelsriver_whatsapp_chat", "noopener,noreferrer");

    if (leadId) {
      const stamp = new Date().toLocaleString("he-IL");
      const noteText = [
        "יצירת קשר בוואטסאפ",
        `טלפון: ${digits}`,
        `נפתח חלון שיחה · ${stamp}`,
      ].join("\n");
      try {
        const res = await createAgentPublicLeadNote(leadId, { note_text: noteText });
        if (res?.note) appendNoteLocal(res.note);
      } catch (err) {
        console.warn("[CrmLeadDetail] whatsapp note failed", err);
      }
    }
  }, [appendNoteLocal, lead, leadId, message]);

  const sendWhatsAppNote = useCallback(async () => {
    const text = String(waComposerText || "").trim();
    if (!text || !leadId) {
      message.warning("יש להזין תוכן הודעה");
      return;
    }
    setWaComposerSaving(true);
    try {
      const res = await createAgentPublicLeadNote(leadId, {
        note_text: `שיחת WhatsApp:\n${text}`,
      });
      if (res?.note) appendNoteLocal(res.note);
      setWaComposerText("");
    } catch (e) {
      message.error(e?.message || "שגיאת רשת");
    } finally {
      setWaComposerSaving(false);
    }
  }, [appendNoteLocal, leadId, message, waComposerText]);

  const reopenWhatsAppOnly = useCallback(() => {
    const phoneRaw = lead?.phone || lead?.linked_booking?.phone || "";
    const url = buildWhatsAppChatUrl(phoneRaw);
    if (!url) {
      message.warning("אין מספר טלפון תקין");
      return;
    }
    window.open(url, "hotelsriver_whatsapp_chat", "noopener,noreferrer");
  }, [lead, message]);

  const openCall = () => {
    const text = String(lead?.phone || lead?.linked_booking?.phone || "").trim();
    if (!text) return;
    window.open(`tel:${text}`, "_self");
  };

  const leftPaneAriaLabel =
    leftPaneMode === LEFT_PANE_WHATSAPP
      ? "שיחת WhatsApp"
      : leftPaneMode === LEFT_PANE_ACTIONS
        ? "פעולות בדוקט"
        : leftPaneMode === LEFT_PANE_OPS
          ? "צ׳אט אופרציה"
          : leftPaneMode === LEFT_PANE_SUMMARY
            ? "סיכום דוקט"
            : "תיעוד";

  if (loading && !lead) {
    return (
      <div className="crm-lead-detail crm-lead-detail--loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!lead) return null;

  const mobileSurfaceClass = !isCrmMobile
    ? ""
    : leftPaneMode === LEFT_PANE_SUMMARY
      ? " crm-lead-detail--mobile crm-lead-detail--mobile-docket"
      : " crm-lead-detail--mobile crm-lead-detail--mobile-pane";

  return (
    <div
      className={`crm-lead-detail${mobileSurfaceClass}`}
      role="dialog"
      aria-label="תיק ליד"
    >
      {canUploadExtraFiles ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            const list = e.target.files;
            if (list?.length) void uploadExtraPdfFiles(list);
            e.target.value = "";
          }}
        />
      ) : null}
      <header className="crm-lead-detail__topbar">
        <Button
          type="text"
          className="crm-lead-detail__back"
          icon={<ArrowRightOutlined />}
          onClick={onClose}
        >
          {backLabel || (isDocket ? "חזרה לדוקטים" : "חזרה ללידים")}
        </Button>
        <div className="crm-lead-detail__topbar-title">
          <span className="crm-lead-detail__file-ref">{formatFileRef(lead)}</span>
          <span className="crm-lead-detail__topbar-name">
            {lead.full_name || "ללא שם"}
          </span>
          <Tag color={lead.status === "new" ? "blue" : "default"}>{statusLabel}</Tag>
        </div>
        <div className="crm-lead-detail__topbar-actions">
          {showCreateOffer ? (
            <Button type="primary" className="crm-lead-detail__cta" onClick={onCreateOffer}>
              צור הצעה
            </Button>
          ) : null}
          <Popconfirm
            title={isDocket ? "למחוק את הדוקט?" : "למחוק את הליד?"}
            description="הפעולה בלתי הפיכה."
            okText="מחק"
            cancelText="ביטול"
            okButtonProps={{ danger: true, loading: deleting }}
            zIndex={CRM_DETAIL_OVERLAY_Z}
            onConfirm={onDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              מחק
            </Button>
          </Popconfirm>
        </div>
      </header>

      <div className="crm-lead-detail__grid">
        {/* Physical LEFT — docs / file actions / ops / (gated) WhatsApp */}
        <section
          className={`crm-lead-detail__chat${
            leftPaneMode === LEFT_PANE_WHATSAPP
              ? " crm-lead-detail__chat--whatsapp"
              : ""
          }`}
          aria-label={leftPaneAriaLabel}
        >
          <div className="crm-lead-detail__pane-head crm-lead-detail__pane-head--split">
            <Segmented
              size="small"
              className="crm-lead-detail__pane-segmented"
              value={
                leftPaneMode === LEFT_PANE_WHATSAPP && !SHOW_CRM_WHATSAPP_PANE
                  ? LEFT_PANE_DOCS
                  : leftPaneMode
              }
              onChange={setLeftPaneMode}
              options={leftPaneSegmentOptions}
            />
            {SHOW_CRM_WHATSAPP_PANE && leftPaneMode === LEFT_PANE_WHATSAPP ? (
              <Button
                type="primary"
                size="small"
                className="crm-wa-reopen-btn"
                icon={<WhatsAppOutlined />}
                onClick={reopenWhatsAppOnly}
                disabled={!lead.phone}
              >
                פתח שיחה
              </Button>
            ) : null}
          </div>

          {SHOW_CRM_WHATSAPP_PANE && leftPaneMode === LEFT_PANE_WHATSAPP ? (
            <>
              <div className="crm-wa-banner">
                שיחה עם {lead.full_name || "הלקוח"}
                {lead.phone ? ` · ${lead.phone}` : ""}
              </div>
              <div className="crm-imsg-thread crm-lead-detail__thread crm-wa-thread">
                {notesLoading ? (
                  <div className="crm-lead-detail__thread-loading">
                    <Spin />
                  </div>
                ) : whatsappNotes.length ? (
                  whatsappNotes.map((note) => (
                    <div key={note.id} className="crm-imsg-row">
                      <div className="crm-imsg-bubble crm-imsg-bubble--mine crm-wa-bubble">
                        {note.note_text || "—"}
                      </div>
                      <div className="crm-imsg-meta">
                        {note.agent_email ||
                          (note.agent_id ? `סוכן #${note.agent_id}` : "סוכן")}
                        {" · "}
                        {note.created_at
                          ? formatShortDate(note.created_at)
                          : "ללא זמן"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="crm-imsg-empty">
                    <Empty
                      description="אין הודעות שיחה עדיין — לחצו WhatsApp בפאנל יצירת קשר"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
              </div>
              <div className="crm-notes-composer crm-lead-detail__composer crm-wa-composer">
                <Input.TextArea
                  value={waComposerText}
                  onChange={(e) => setWaComposerText(e.target.value)}
                  rows={2}
                  placeholder="כתבו כאן סיכום / הודעה לתיעוד השיחה..."
                  maxLength={4000}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void sendWhatsAppNote();
                    }
                  }}
                />
                <Button
                  className="crm-notes-send-btn crm-wa-send-btn"
                  type="primary"
                  loading={waComposerSaving}
                  disabled={!String(waComposerText || "").trim()}
                  onClick={() => void sendWhatsAppNote()}
                >
                  שמור בשיחה
                </Button>
              </div>
            </>
          ) : leftPaneMode === LEFT_PANE_OPS ? (
            <CrmEmbeddedOpsPane
              linkedBooking={linkedBooking}
              initialOpsType={initialOpsType}
              initialOpsRequestId={
                initialOpsRequestId ||
                linkedBooking?.ops_request_id ||
                linkedBooking?.opsRequestId ||
                extractOpsRequestIdFromCrmLead(lead) ||
                null
              }
              leadAgentId={lead?.agent_id ?? lead?.agentId ?? null}
            />
          ) : leftPaneMode === LEFT_PANE_SUMMARY ? null : leftPaneMode === LEFT_PANE_ACTIONS ? (
            <div className="crm-file-actions crm-file-actions--journal-only" dir="rtl">
              <div className="crm-file-actions__section crm-file-actions__audit">
                <div className="crm-file-actions__title">יומן פעולות</div>
                <div className="crm-imsg-thread crm-file-actions__audit-thread">
                  {notesLoading ? (
                    <div className="crm-lead-detail__thread-loading">
                      <Spin />
                    </div>
                  ) : actionAuditNotes.length ? (
                    actionAuditNotes.map((note) => {
                      const isLegacy =
                        String(note.id || "").startsWith("legacy-") ||
                        String(note.note_type || "") === "system_seed" ||
                        String(note.note_text || "").includes(
                          "תיק נפתח אוטומטית"
                        );
                      return (
                        <div
                          key={note.id}
                          className={`crm-imsg-row${
                            isLegacy ? " crm-imsg-row--other" : ""
                          }`}
                        >
                          <div
                            className={`crm-imsg-bubble${
                              isLegacy
                                ? " crm-imsg-bubble--other"
                                : " crm-imsg-bubble--mine"
                            }`}
                          >
                            {note.note_text || "—"}
                          </div>
                          <div className="crm-imsg-meta">
                            {isLegacy
                              ? "מערכת"
                              : note.agent_email ||
                                note.author_email ||
                                (note.agent_id
                                  ? `סוכן #${note.agent_id}`
                                  : "סוכן")}
                            {" · "}
                            {note.created_at
                              ? formatShortDate(note.created_at)
                              : "ללא זמן"}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="crm-imsg-empty">
                      <Empty
                        description="אין פעולות בדוקט עדיין"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="crm-docs-pane" dir="rtl">
              <div className="crm-imsg-thread crm-lead-detail__thread">
                {notesLoading ? (
                  <div className="crm-lead-detail__thread-loading">
                    <Spin />
                  </div>
                ) : correspondenceNotes.length ? (
                  correspondenceNotes.map((note) => (
                    <div key={note.id} className="crm-imsg-row">
                      <div className="crm-imsg-bubble crm-imsg-bubble--mine">
                        {note.note_text || "—"}
                      </div>
                      <div className="crm-imsg-meta">
                        {note.agent_email ||
                          (note.agent_id ? `סוכן #${note.agent_id}` : "סוכן")}
                        {" · "}
                        {note.created_at
                          ? formatShortDate(note.created_at)
                          : "ללא זמן"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="crm-imsg-empty">
                    <Empty
                      description="אין תיעוד עדיין"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
              </div>
              <div className="crm-notes-composer crm-lead-detail__composer">
                <Input.TextArea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  rows={2}
                  placeholder="כתוב הודעה..."
                  maxLength={4000}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void addNote();
                    }
                  }}
                />
                <div className="crm-notes-composer__actions">
                  {canUploadExtraFiles ? (
                    <Button
                      type="text"
                      size="small"
                      loading={extraFileUploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      העלאת PDF
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Button
                    className="crm-notes-send-btn"
                    type="primary"
                    loading={notesSaving}
                    disabled={!String(newNoteText || "").trim()}
                    onClick={addNote}
                  >
                    שלח הודעה
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
        {/* Physical RIGHT — customer docket */}
        <section className="crm-lead-detail__docket" aria-label="תיק לקוח" dir="rtl">
          <div className="crm-docket">
            <div className="crm-docket__banner">
              {lead.linked_booking ? "תיק לקוח · הזמנה" : "תיק לקוח · ליד"}
            </div>

            {lead.linked_booking ? (
              <div className="crm-docket__snap" aria-label="תאריכים וסכום תשלום">
                <div className="crm-docket__snap-item">
                  <div className="crm-docket__snap-label">תאריכי שהייה</div>
                  <div className="crm-docket__snap-value">
                    {buildStayDatesWithWeekdays(lead.linked_booking)}
                  </div>
                </div>
                <div className="crm-docket__snap-item crm-docket__snap-item--paid">
                  <div className="crm-docket__snap-label">{heroAmountLabel}</div>
                  <div className="crm-docket__snap-value crm-docket__snap-value--paid">
                    {heroAmountDisplay}
                  </div>
                  {paymentReceipt?.total_due_display ? (
                    <div className="crm-docket__snap-sub">
                      סה״כ הזמנה: {paymentReceipt.total_due_display}
                      {paymentReceipt.amount_paid_display
                        ? ` · שולם: ${paymentReceipt.amount_paid_display}`
                        : ""}
                      {paymentReceipt.payment_complete === false &&
                      paymentReceipt.balance_due_display
                        ? ` · יתרה: ${paymentReceipt.balance_due_display}`
                        : ""}
                    </div>
                  ) : null}
                  {paymentReceipt?.agent_commission_display ? (
                    <div className="crm-docket__snap-sub">
                      עמלת סוכן: {paymentReceipt.agent_commission_display}
                    </div>
                  ) : null}
                  {paymentReceipt?.baggage_display ? (
                    <div className="crm-docket__snap-sub">
                      כבודה ותוספות: {paymentReceipt.baggage_display}
                    </div>
                  ) : null}
                  {paymentReceipt?.hr_commission_display ? (
                    <div className="crm-docket__snap-sub">
                      עמלת HR: {paymentReceipt.hr_commission_display}
                    </div>
                  ) : null}
                  {paymentReceipt?.fx_rate_display ? (
                    <div className="crm-docket__snap-sub">
                      שער דולר {paymentReceipt.fx_rate_display}
                    </div>
                  ) : null}
                  {paymentReceipt?.bank_transfer &&
                  paymentReceipt?.payment_complete === false ? (
                    <div className="crm-docket__snap-sub">העברה בנקאית — ממתין להשלמת יתרה</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="crm-docket__identity">
              <div className="crm-docket__block">
                <div className="crm-docket__label">לקוח</div>
                <div className="crm-docket__value crm-docket__value--lg">
                  {lead.full_name || "—"}
                </div>
                <div className="crm-docket__sub">{statusLabel}</div>
              </div>
              <div className="crm-docket__block">
                <div className="crm-docket__label">יעד / עניין</div>
                <div className="crm-docket__value">
                  {lead.linked_booking?.interest_destination ||
                    lead.linked_booking?.hotel_name ||
                    lead.interest_destination ||
                    "לא צוין"}
                </div>
                <div className="crm-docket__sub">
                  {formatPublicLeadCategory(
                    lead.linked_booking?.deal_type_label || lead.category
                  )}
                </div>
              </div>
              <div className="crm-docket__block">
                <div className="crm-docket__label">מס׳ תיק</div>
                <div className="crm-docket__value">{formatFileRef(lead)}</div>
                <div className="crm-docket__sub">
                  נפתח {formatShortDate(lead.created_at)}
                </div>
              </div>
            </div>

            <div className="crm-docket__section">
              <div className="crm-docket__section-title">יצירת קשר</div>
              {contactEditing ? (
                <div className="crm-docket__contact-edit">
                  <div>
                    <div className="crm-docket__label">טלפון</div>
                    <Input
                      value={contactPhoneDraft}
                      onChange={(e) => setContactPhoneDraft(e.target.value)}
                      placeholder="05XXXXXXXX"
                      allowClear
                    />
                  </div>
                  <div>
                    <div className="crm-docket__label">אימייל</div>
                    <Input
                      type="email"
                      value={contactEmailDraft}
                      onChange={(e) => setContactEmailDraft(e.target.value)}
                      placeholder="name@example.com"
                      allowClear
                    />
                  </div>
                  <div className="crm-docket__contact-actions">
                    <Button onClick={() => setContactEditing(false)} disabled={contactSaving}>
                      ביטול
                    </Button>
                    <Button type="primary" loading={contactSaving} onClick={() => void saveContact()}>
                      שמירה
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="crm-docket__contact-row">
                  <div>
                    <div className="crm-docket__label">טלפון</div>
                    <div className="crm-docket__value">{displayPhone || "—"}</div>
                  </div>
                  <div>
                    <div className="crm-docket__label">אימייל</div>
                    <div className="crm-docket__value">{displayEmail || "—"}</div>
                  </div>
                  <div className="crm-docket__contact-actions">
                    <Tooltip title="עריכת פרטי קשר">
                      <Button icon={<EditOutlined />} onClick={beginContactEdit} />
                    </Tooltip>
                    <Tooltip title="התקשר">
                      <Button
                        icon={<PhoneOutlined />}
                        onClick={openCall}
                        disabled={!displayPhone}
                      />
                    </Tooltip>
                    <Tooltip title="WhatsApp">
                      <Button
                        icon={<WhatsAppOutlined />}
                        onClick={openWhatsApp}
                        disabled={!displayPhone}
                      />
                    </Tooltip>
                  </div>
                  {contactMissing ? (
                    <div className="crm-docket__contact-hint">
                      חסרים פרטי קשר — ניתן לערוך ולשמור בתיק
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {lead.linked_booking ? (
              <div
                className="crm-docket__section crm-docket__payments-received"
                aria-label="תשלומים שהתקבלו"
              >
                <div className="crm-docket__section-title-row">
                  <div className="crm-docket__section-title">תשלומים שהתקבלו</div>
                  {canManagePaymentEntries ? (
                    <Tooltip title="הוספת תשלום שהתקבל">
                      <Button
                        type="text"
                        size="small"
                        className="crm-docket__pay-add-btn"
                        icon={<PlusOutlined />}
                        aria-label="הוספת תשלום שהתקבל"
                        onClick={openPaymentAddModal}
                      />
                    </Tooltip>
                  ) : null}
                </div>
                {paymentEntriesLoading && paymentsReceived.length === 0 ? (
                  <p className="crm-docket__placeholder">טוען תשלומים…</p>
                ) : paymentsReceived.length > 0 ? (
                  <ul className="crm-docket__pay-rows">
                    {paymentsReceived.map((row) => {
                      const metaBits = [];
                      if (row.direction_label && row.source === "manual") {
                        metaBits.push(row.direction_label);
                      }
                      if (row.installments != null && Number(row.installments) > 0) {
                        metaBits.push(
                          Number(row.installments) === 1
                            ? "תשלום אחד"
                            : `${row.installments} תשלומים`
                        );
                      }
                      if (row.paid_at) {
                        metaBits.push(formatShortDate(row.paid_at));
                      }
                      if (row.transaction_id) {
                        metaBits.push(`TransId ${row.transaction_id}`);
                      }
                      if (row.note) {
                        metaBits.push(String(row.note));
                      }
                      const busy =
                        paymentRowBusyKey != null &&
                        String(paymentRowBusyKey) === String(row.key || "");
                      const amountClass =
                        row.direction === "debit" ||
                        (Number(row.signed_amount_ils) || 0) < 0
                          ? "crm-docket__pay-amount crm-docket__pay-amount--debit"
                          : "crm-docket__pay-amount";
                      return (
                        <li
                          key={row.key || `${row.method}-${row.index}-${row.amount_ils}`}
                          className={`crm-docket__pay-row crm-docket__pay-row--${row.method || "other"}${
                            row.source === "manual" ? " crm-docket__pay-row--manual-entry" : ""
                          }`}
                        >
                          <div className="crm-docket__pay-row-main">
                            <span className="crm-docket__pay-method">
                              {row.method_label || "תשלום"}
                              {row.source_badge ? (
                                <span
                                  className={`crm-docket__pay-badge crm-docket__pay-badge--${row.source || "order"}`}
                                >
                                  {row.source_badge}
                                </span>
                              ) : null}
                            </span>
                            <span className={amountClass}>
                              {row.amount_display || "—"}
                            </span>
                          </div>
                          {metaBits.length > 0 ? (
                            <div className="crm-docket__pay-meta">
                              {metaBits.join(" · ")}
                            </div>
                          ) : null}
                          {canManagePaymentEntries && row.cancellable ? (
                            <div className="crm-docket__pay-row-actions">
                              <Popconfirm
                                title={
                                  row.cancel_mode === "hide_auto"
                                    ? "להסתיר שורה זו מהדוקט?"
                                    : "לבטל רשומת תשלום זו?"
                                }
                                description={
                                  row.cancel_mode === "hide_auto"
                                    ? "שורת ההזמנה תוסתר בדוקט בלבד — לא משנה את ההזמנה."
                                    : "הביטול נשמר ביומן הדוקט ואינו משנה את ההזמנה."
                                }
                                okText="מחיקה / ביטול"
                                cancelText="חזרה"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => void cancelPaymentReceivedRow(row)}
                                zIndex={CRM_DETAIL_OVERLAY_Z + 20}
                              >
                                <Button
                                  type="link"
                                  size="small"
                                  danger
                                  loading={busy}
                                  className="crm-docket__pay-cancel-btn"
                                >
                                  מחיקה / ביטול
                                </Button>
                              </Popconfirm>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="crm-docket__placeholder">
                    {canManagePaymentEntries
                      ? "אין פירוט תשלומים רשומים — ניתן להוסיף עם +"
                      : "אין פירוט תשלומים רשומים"}
                  </p>
                )}
              </div>
            ) : null}

            <div className="crm-docket__section">
              <div className="crm-docket__section-title">מקור וסטטוס</div>
              <div className="crm-docket__meta-grid">
                <div>
                  <div className="crm-docket__label">מקור</div>
                  <span className={`crm-badge ${sourceMeta.className}`}>
                    {sourceMeta.label}
                  </span>
                  {sourceMeta.detail ? (
                    <div className="crm-lead-source__detail" title={sourceMeta.detail}>
                      {sourceMeta.detail}
                    </div>
                  ) : null}
                  {lead.source_url ? (
                    <div className="crm-docket__link">
                      <a href={lead.source_url} target="_blank" rel="noreferrer noopener">
                        עמוד מקור
                      </a>
                    </div>
                  ) : null}
                </div>
                <div>
                  <div className="crm-docket__label">סטטוס טיפול</div>
                  <Select
                    style={{ width: "100%", maxWidth: 200 }}
                    value={lead.status || "new"}
                    options={STATUS_OPTIONS}
                    loading={statusSaving}
                    onChange={onStatusChange}
                  />
                </div>
              </div>
            </div>

            <div className="crm-docket__section">
              <div className="crm-docket__section-title">סיכום AI / עניין</div>
              <p className="crm-docket__summary">
                {lead.linked_booking?.ai_sales_note ||
                  lead.ai_sales_note ||
                  lead.message ||
                  "אין סיכום עדיין."}
              </p>
              {(lead.linked_booking?.message || lead.message) &&
              (lead.linked_booking?.ai_sales_note || lead.ai_sales_note) ? (
                <p className="crm-docket__message">
                  <strong>הודעת לקוח: </strong>
                  {lead.linked_booking?.message || lead.message}
                </p>
              ) : null}
            </div>

            {manoQuotesForLead.length > 0 || manoQuotesLoading ? (
              <div className="crm-docket__section">
                <div className="crm-docket__section-title">הצעות מנו ספנות</div>
                {manoQuotesLoading ? (
                  <Spin size="small" />
                ) : (
                  <ul className="crm-docket__rooms-list">
                    {manoQuotesForLead.map((q) => (
                      <li key={q.id}>
                        <div className="crm-docket__value">
                          {q.quote_number || `טיוטה #${q.id}`}
                          {q.destinations_text
                            ? ` · ${q.destinations_text}`
                            : ""}
                          {q.final_total_price != null
                            ? ` · ₪${Number(q.final_total_price).toLocaleString("he-IL")}`
                            : ""}
                        </div>
                        <div className="crm-docket__sub">
                          סטטוס: {q.effective_status || q.status || "—"}
                        </div>
                        {q.status !== "archived" ? (
                          <Button
                            size="small"
                            type="primary"
                            icon={<CreditCardOutlined />}
                            loading={manoPayBusyId === q.id}
                            style={{ marginTop: 6, marginLeft: 8 }}
                            onClick={() => onPayManoQuote(q)}
                          >
                            תשלום מנו
                          </Button>
                        ) : null}
                        {q.status !== "closed" && q.status !== "archived" ? (
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={manoCloseBusyId === q.id}
                            style={{ marginTop: 6 }}
                            onClick={() => onCloseManoQuoteAsDocket(q)}
                          >
                            סמן כנסגרה → דוקט
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}

            {lead.linked_booking ? (
              <>
                <div className="crm-docket__section crm-docket__booking">
                  <div className="crm-docket__section-title">הזמנה מקושרת</div>
                  <div className="crm-docket__booking-grid">
                    <div>
                      <div className="crm-docket__label">מלון / שירות</div>
                      <div className="crm-docket__value">
                        {lead.linked_booking.hotel_name || "—"}
                      </div>
                      {bookingRooms.length > 1 ? (
                        <ul className="crm-docket__rooms-list">
                          {bookingRooms.map((room) => (
                            <li key={`room-${room.index}`}>
                              <strong>חדר {room.index}:</strong> {room.room_type}
                              {room.pax_summary ? (
                                <span className="crm-docket__sub">
                                  {" "}
                                  · {room.pax_summary}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : lead.linked_booking.room_type ? (
                        <div className="crm-docket__sub">
                          {lead.linked_booking.room_type}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="crm-docket__label">תאריכים</div>
                      <div className="crm-docket__value">
                        {buildStayDatesWithWeekdays(lead.linked_booking)}
                      </div>
                      <div className="crm-docket__sub">
                        {lead.linked_booking.deal_type_label ||
                          lead.linked_booking.deal_type ||
                          "הזמנה"}
                      </div>
                    </div>
                    <div>
                      <div className="crm-docket__label">הרכב</div>
                      <div className="crm-docket__value">
                        {lead.linked_booking.pax_summary || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="crm-docket__label">סכום / סטטוס</div>
                      <div className="crm-docket__value">
                        {lead.linked_booking.amount_display || "—"}
                      </div>
                      <div className="crm-docket__sub">
                        {lead.linked_booking.status_label || "—"}
                      </div>
                      {paymentReceipt?.agent_commission_display ? (
                        <div className="crm-docket__sub">
                          עמלת סוכן: {paymentReceipt.agent_commission_display}
                        </div>
                      ) : null}
                      {paymentReceipt?.baggage_display ? (
                        <div className="crm-docket__sub">
                          כבודה: {paymentReceipt.baggage_display}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="crm-docket__label">מס׳ הזמנה</div>
                      <div className="crm-docket__value crm-docket__value--mono">
                        {lead.linked_booking.booking_ref ||
                          lead.linked_booking.order_id?.slice(0, 8) ||
                          "—"}
                      </div>
                    </div>
                  </div>
                  {bookingFlights.length ? (
                    <div className="crm-docket__flights">
                      <div className="crm-docket__label">טיסות</div>
                      <ul className="crm-docket__flights-list">
                        {bookingFlights.map((leg, idx) => (
                          <li key={`flight-${idx}-${leg.flight_number || ""}`}>
                            <div className="crm-docket__value">
                              {[
                                leg.airline,
                                leg.flight_number,
                                leg.route_label,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "טיסה"}
                            </div>
                            {leg.schedule_label ? (
                              <div className="crm-docket__sub">{leg.schedule_label}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                <div className="crm-docket__section crm-docket__receipt">
                  <div className="crm-docket__section-title">
                    מסמכים ללקוח
                  </div>
                  {paymentReceipt ? (
                    <div className="crm-docket__booking-grid crm-docket__receipt-meta">
                      <div>
                        <div className="crm-docket__label">
                          {paymentReceipt.payment_complete === false
                            ? "סכום שנרשם / שולם"
                            : "סכום שחויב"}
                        </div>
                        <div className="crm-docket__value">
                          {paymentReceipt.payment_complete === false
                            ? paymentReceipt.amount_paid_display ||
                              paymentReceipt.charged_amount_display ||
                              "—"
                            : paymentReceipt.total_due_display ||
                              paymentReceipt.charged_amount_display ||
                              paymentReceipt.amount_display ||
                              lead.linked_booking.amount_display ||
                              "—"}
                        </div>
                      </div>
                      {paymentReceipt.j5_undercharge &&
                      paymentReceipt.original_amount_display ? (
                        <div>
                          <div className="crm-docket__label">סכום עסקה מקורי</div>
                          <div className="crm-docket__value">
                            {paymentReceipt.original_amount_display}
                          </div>
                          {paymentReceipt.j5_amount_delta_display ? (
                            <div className="crm-docket__sub">
                              הפרש: {paymentReceipt.j5_amount_delta_display}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div>
                          <div className="crm-docket__label">סכום הזמנה</div>
                          <div className="crm-docket__value">
                            {lead.linked_booking.amount_display || "—"}
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="crm-docket__label">תאריך תשלום</div>
                        <div className="crm-docket__value">
                          {paymentReceipt.paid_at
                            ? formatShortDate(paymentReceipt.paid_at)
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="crm-docket__label">מס׳ אישור / TransId</div>
                        <div className="crm-docket__value crm-docket__value--mono">
                          {paymentReceipt.auth_number || "—"}
                          {paymentReceipt.transaction_id
                            ? ` · ${paymentReceipt.transaction_id}`
                            : ""}
                        </div>
                      </div>
                      <div>
                        <div className="crm-docket__label">תשלומים</div>
                        <div className="crm-docket__value">
                          {paymentReceipt.installments
                            ? `${paymentReceipt.installments}`
                            : "1"}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {paymentReceipt?.j5_undercharge && paymentReceipt?.j5_amount_note ? (
                    <div className="crm-docket__j5-amount-note" role="status">
                      {paymentReceipt.j5_amount_note}
                    </div>
                  ) : null}

                  <div className="crm-docket__send-box">
                    {clientEmail ? (
                      <div className="crm-docket__client-email-ref">
                        <span className="crm-docket__client-email-ref-label">
                          מייל לקוח מקורי:
                        </span>
                        <code className="crm-docket__client-email-ref-value">
                          {clientEmail}
                        </code>
                        <Tooltip title="העתק מייל לקוח">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => void copyClientEmail()}
                            aria-label="העתק מייל לקוח"
                          />
                        </Tooltip>
                        <Button
                          type="link"
                          size="small"
                          onClick={applyClientEmailToPrimary}
                        >
                          השתמש בשורה הראשית
                        </Button>
                      </div>
                    ) : (
                      <div className="crm-docket__client-email-ref crm-docket__client-email-ref--empty">
                        אין מייל לקוח שמור בתיק — ניתן להזין ידנית למטה
                      </div>
                    )}
                    <div className="crm-docket__email-rows">
                      {(emailRows.length ? emailRows : [""]).map(
                        (rowEmail, index) => (
                          <div
                            key={`email-row-${index}`}
                            className="crm-docket__email-row"
                          >
                            <Input
                              type="email"
                              value={rowEmail}
                              onChange={(e) =>
                                updateEmailRow(index, e.target.value)
                              }
                              placeholder={
                                index === 0
                                  ? "מייל ראשי לשליחה (ניתן לעריכה)"
                                  : "מייל נוסף"
                              }
                              prefix={<MailOutlined />}
                              allowClear
                            />
                            {index === 0 ? (
                              <Button
                                type="default"
                                icon={<PlusOutlined />}
                                onClick={addEmailRow}
                                aria-label="הוסף מייל"
                                title="הוסף מייל"
                              />
                            ) : (
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => removeEmailRow(index)}
                                aria-label="הסר מייל"
                                title="הסר מייל"
                              />
                            )}
                          </div>
                        )
                      )}
                    </div>
                    <div className="crm-docket__receipt-actions crm-docket__send-actions">
                      <Button
                        type="primary"
                        icon={<MailOutlined />}
                        loading={sendSaving === "confirmation"}
                        onClick={() => sendDocuments("confirmation")}
                      >
                        שלח אישור הזמנה
                      </Button>
                      <Button
                        icon={<MailOutlined />}
                        loading={sendSaving === "receipt"}
                        onClick={() => sendDocuments("receipt")}
                      >
                        שלח קבלה / חשבונית
                      </Button>
                      <Button
                        icon={<MailOutlined />}
                        loading={sendSaving === "hotel_voucher"}
                        onClick={() => sendDocuments("hotel_voucher")}
                      >
                        שלח שובר מלון
                      </Button>
                      <Button
                        icon={<MailOutlined />}
                        loading={sendSaving === "order_summary"}
                        onClick={() => {
                          if (!linkedOrderId) {
                            openOrderSummaryModal();
                            return;
                          }
                          sendDocuments("order_summary");
                        }}
                      >
                        שלח סיכום הזמנה
                      </Button>
                      <Button
                        icon={<MailOutlined />}
                        loading={sendSaving === "bundle"}
                        onClick={openSendFilesModal}
                      >
                        שלח קבצים
                      </Button>
                    </div>
                  </div>

                  {canUploadExtraFiles ? (
                    <div
                      className={`crm-docket__upload${
                        composerDragOver ? " crm-docket__upload--dragover" : ""
                      }`}
                      onDragEnter={onComposerDragOver}
                      onDragOver={onComposerDragOver}
                      onDragLeave={onComposerDragLeave}
                      onDrop={onComposerDrop}
                    >
                      <div className="crm-docket__section-title crm-docket__section-title--inline">
                        העלאת קבצים
                      </div>
                      <div className="crm-docket__receipt-actions">
                        <Button
                          type="primary"
                          loading={extraFileUploading}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          העלה PDF
                        </Button>
                      </div>
                      {composerDragOver || extraFileUploading ? (
                        <div className="crm-docket__upload-hint" aria-live="polite">
                          {extraFileUploading
                            ? "מעלה PDF לשרת..."
                            : "שחררו כאן קובץ PDF"}
                        </div>
                      ) : (
                        <p className="crm-docket__placeholder">
                          ניתן גם לגרור PDF לכאן — יופיע בתצוגה המקדימה למטה.
                        </p>
                      )}
                    </div>
                  ) : null}

                  <div className="crm-docket__receipt-actions">
                    <Button
                      icon={<DownloadOutlined />}
                      loading={receiptLoading}
                      onClick={downloadReceiptPdf}
                    >
                      הורד קבלה
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      loading={confirmationLoading}
                      onClick={downloadBookingConfirmation}
                    >
                      הורד אישור הזמנה
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      loading={hotelVoucherLoading}
                      onClick={downloadHotelVoucher}
                    >
                      הורד שובר מלון
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      loading={orderSummaryLoading}
                      onClick={downloadOrderSummary}
                    >
                      הורד סיכום הזמנה
                    </Button>
                    <Button
                      icon={<EyeOutlined />}
                      loading={
                        receiptLoading ||
                        confirmationLoading ||
                        hotelVoucherLoading ||
                        orderSummaryLoading ||
                        extraFilePreviewLoading
                      }
                      onClick={() => {
                        if (docPreviewTab === "extra") {
                          if (extraFilePreviewUrl) {
                            window.open(
                              extraFilePreviewUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else if (selectedExtraFileId) {
                            void loadExtraFilePreview(selectedExtraFileId).then(
                              (url) => {
                                if (url)
                                  window.open(url, "_blank", "noopener,noreferrer");
                              }
                            );
                          }
                          return;
                        }
                        if (docPreviewTab === "confirmation") {
                          if (confirmationPdfUrl) {
                            window.open(
                              confirmationPdfUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else {
                            void loadConfirmationPdf().then((url) => {
                              if (url)
                                window.open(url, "_blank", "noopener,noreferrer");
                            });
                          }
                          return;
                        }
                        if (docPreviewTab === "order_summary") {
                          if (orderSummaryPdfUrl) {
                            window.open(
                              orderSummaryPdfUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else {
                            void loadOrderSummaryPdf().then((url) => {
                              if (url)
                                window.open(url, "_blank", "noopener,noreferrer");
                            });
                          }
                          return;
                        }
                        if (docPreviewTab === "hotel_voucher") {
                          if (hotelVoucherPdfUrl) {
                            window.open(
                              hotelVoucherPdfUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else {
                            void loadHotelVoucherPdf().then((url) => {
                              if (url)
                                window.open(url, "_blank", "noopener,noreferrer");
                            });
                          }
                          return;
                        }
                        void openReceiptPdf();
                      }}
                    >
                      פתח בחלון חדש
                    </Button>
                  </div>

                  <div className="crm-docket__preview-switch">
                    <Segmented
                      value={
                        previewSegmentOptions.some((o) => o.value === docPreviewTab)
                          ? docPreviewTab
                          : "receipt"
                      }
                      onChange={(val) => {
                        setDocPreviewTab(val);
                        if (
                          val === "extra" &&
                          !selectedExtraFileId &&
                          extraFiles[0]?.id
                        ) {
                          setSelectedExtraFileId(String(extraFiles[0].id));
                        }
                      }}
                      options={previewSegmentOptions}
                      block
                    />
                  </div>

                  {docPreviewTab === "extra" && extraFiles.length > 0 ? (
                    <div className="crm-docket__extra-files">
                      {extraFiles.map((f) => {
                        const id = String(f.id || "");
                        const active = id === String(selectedExtraFileId || "");
                        return (
                          <Button
                            key={id}
                            size="small"
                            type={active ? "primary" : "default"}
                            onClick={() => {
                              setSelectedExtraFileId(id);
                              setDocPreviewTab("extra");
                            }}
                          >
                            {f.original_filename || "קובץ PDF"}
                          </Button>
                        );
                      })}
                    </div>
                  ) : null}

                  <div className="crm-docket__receipt-preview">
                    {docPreviewTab === "receipt" ? (
                      receiptLoading ? (
                        <div className="crm-docket__receipt-loading">
                          <Spin tip="טוען קבלת יעד שריג..." />
                        </div>
                      ) : receiptError ? (
                        <div className="crm-docket__receipt-error">
                          <p>{receiptError}</p>
                          {/J5|CCode 700|תבנית קבלה|עדיין לא הופק/.test(
                            String(receiptError)
                          ) ? (
                            <p className="crm-docket__receipt-error-note">
                              הרשאת J5 (CCode 700) אינה חשבונית. יעד שריג מפיקים
                              קבלה על עסקת החיוב הסופי אחרי מימוש.
                            </p>
                          ) : null}
                          <Button size="small" onClick={() => void loadReceiptPdf()}>
                            נסה שוב
                          </Button>
                        </div>
                      ) : receiptPdfUrl ? (
                        <iframe
                          title="הצג קבלה"
                          src={receiptPdfUrl}
                          className="crm-docket__receipt-frame"
                        />
                      ) : (
                        <p className="crm-docket__placeholder">
                          אין תצוגה מקדימה לקבלה.
                        </p>
                      )
                    ) : docPreviewTab === "extra" ? (
                      extraFilePreviewLoading || extraFilesLoading ? (
                        <div className="crm-docket__receipt-loading">
                          <Spin tip="טוען קובץ נוסף..." />
                        </div>
                      ) : extraFilePreviewUrl ? (
                        <iframe
                          title={
                            selectedExtraFile?.original_filename || "קובץ נוסף"
                          }
                          src={extraFilePreviewUrl}
                          className="crm-docket__receipt-frame"
                        />
                      ) : (
                        <p className="crm-docket__placeholder">
                          אין קבצים נוספים עדיין
                          {canUploadExtraFiles
                            ? " — העלו PDF למעלה בתיק הלקוח."
                            : "."}
                        </p>
                      )
                    ) : docPreviewTab === "hotel_voucher" ? (
                      hotelVoucherLoading ? (
                        <div className="crm-docket__receipt-loading">
                          <Spin tip="טוען שובר מלון..." />
                        </div>
                      ) : hotelVoucherError ? (
                        <div className="crm-docket__receipt-error">
                          <p>{hotelVoucherError}</p>
                          <Button
                            size="small"
                            onClick={() => void loadHotelVoucherPdf()}
                          >
                            נסה שוב
                          </Button>
                        </div>
                      ) : hotelVoucherPdfUrl ? (
                        <iframe
                          title="הצג שובר מלון"
                          src={hotelVoucherPdfUrl}
                          className="crm-docket__receipt-frame"
                        />
                      ) : (
                        <p className="crm-docket__placeholder">
                          אין תצוגה מקדימה לשובר המלון.
                        </p>
                      )
                    ) : docPreviewTab === "order_summary" ? (
                      orderSummaryLoading ? (
                        <div className="crm-docket__receipt-loading">
                          <Spin tip="טוען סיכום הזמנה..." />
                        </div>
                      ) : orderSummaryError ? (
                        <div className="crm-docket__receipt-error">
                          <p>{orderSummaryError}</p>
                          <Button
                            size="small"
                            onClick={() => void loadOrderSummaryPdf()}
                          >
                            נסה שוב
                          </Button>
                        </div>
                      ) : orderSummaryPdfUrl ? (
                        <iframe
                          title="הצג סיכום הזמנה"
                          src={orderSummaryPdfUrl}
                          className="crm-docket__receipt-frame"
                        />
                      ) : (
                        <p className="crm-docket__placeholder">
                          אין תצוגה מקדימה לסיכום ההזמנה.
                        </p>
                      )
                    ) : confirmationLoading ? (
                      <div className="crm-docket__receipt-loading">
                        <Spin tip="טוען אישור הזמנה..." />
                      </div>
                    ) : confirmationError ? (
                      <div className="crm-docket__receipt-error">
                        <p>{confirmationError}</p>
                        <Button
                          size="small"
                          onClick={() => void loadConfirmationPdf()}
                        >
                          נסה שוב
                        </Button>
                      </div>
                    ) : confirmationPdfUrl ? (
                      <iframe
                        title="הצג אישור הזמנה"
                        src={confirmationPdfUrl}
                        className="crm-docket__receipt-frame"
                      />
                    ) : (
                      <p className="crm-docket__placeholder">
                        אין תצוגה מקדימה לאישור ההזמנה.
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : manoQuoteId ? (
              <div className="crm-docket__section crm-docket__receipt">
                <div className="crm-docket__section-title">מסמכים ללקוח</div>
                <div className="crm-docket__receipt-actions crm-docket__send-actions">
                  <Button
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={openOrderSummaryModal}
                  >
                    סיכום הזמנה
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={orderSummaryLoading}
                    onClick={() => void downloadOrderSummary()}
                  >
                    הורד סיכום הזמנה
                  </Button>
                </div>
                <div className="crm-docket__receipt-preview" style={{ marginTop: 12 }}>
                  {orderSummaryLoading ? (
                    <div className="crm-docket__receipt-loading">
                      <Spin tip="טוען סיכום הזמנה..." />
                    </div>
                  ) : orderSummaryError ? (
                    <div className="crm-docket__receipt-error">
                      <p>{orderSummaryError}</p>
                      <Button
                        size="small"
                        onClick={() => void loadOrderSummaryPdf()}
                      >
                        נסה שוב
                      </Button>
                    </div>
                  ) : orderSummaryPdfUrl ? (
                    <iframe
                      title="הצג סיכום הזמנה"
                      src={orderSummaryPdfUrl}
                      className="crm-docket__receipt-frame"
                    />
                  ) : (
                    <p className="crm-docket__placeholder">
                      טוען תצוגה מקדימה של סיכום ההזמנה…
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="crm-docket__section crm-docket__receipt">
                <div className="crm-docket__section-title">מסמכים ללקוח</div>
                <p className="crm-docket__placeholder" style={{ marginBottom: 12 }}>
                  אין עדיין הזמנה מקושרת במערכת — ניתן לשלוח סיכום הזמנה ידני
                  (למשל מנו ספנות) עם לוגו הסוכנות.
                </p>
                <div className="crm-docket__receipt-actions crm-docket__send-actions">
                  <Button
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={openOrderSummaryModal}
                  >
                    סיכום הזמנה
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>

      <Modal
        title="סיכום הזמנה ללקוח"
        open={orderSummaryModalOpen}
        onCancel={() => setOrderSummaryModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        width={640}
        zIndex={CRM_DETAIL_OVERLAY_Z}
        getContainer={() => document.body}
      >
        <p className="crm-docket__send-files-hint">
          מלאו את פרטי ההזמנה — המסמך מעוצב כמו הצעת מנו, עם לוגו הסוכנות ומלל משפטי
          כחלק מסיכום ההזמנה שנשלח ללקוח.
        </p>
        <div className="crm-docket__pay-form">
          {ORDER_SUMMARY_FORM_FIELDS.map((field) => (
            <label key={field.key} className="crm-docket__pay-form-field">
              <span>{field.label}</span>
              {field.multiline || field.key === "passengers" ? (
                <Input.TextArea
                  rows={field.rows || 3}
                  value={orderSummaryFields?.[field.key] || ""}
                  onChange={(e) =>
                    updateOrderSummaryField(field.key, e.target.value)
                  }
                  placeholder={field.placeholder}
                />
              ) : (
                <Input
                  value={orderSummaryFields?.[field.key] || ""}
                  onChange={(e) =>
                    updateOrderSummaryField(field.key, e.target.value)
                  }
                  placeholder={field.placeholder}
                />
              )}
            </label>
          ))}
          <label className="crm-docket__pay-form-field">
            <span>מלל משפטי (חלק מסיכום ההזמנה)</span>
            <Input.TextArea
              rows={6}
              value={orderSummaryFields?.legal_text || ""}
              onChange={(e) =>
                updateOrderSummaryField("legal_text", e.target.value)
              }
              placeholder="תנאים והערות משפטיות שיופיעו בסוף המסמך"
            />
          </label>
          <label className="crm-docket__pay-form-field">
            <span>מייל לשליחה</span>
            <Input
              type="email"
              value={orderSummarySendEmail}
              onChange={(e) => setOrderSummarySendEmail(e.target.value)}
              placeholder="client@example.com"
              prefix={<MailOutlined />}
            />
          </label>
        </div>
        <div
          className="crm-docket__receipt-actions"
          style={{ marginTop: 16, flexWrap: "wrap", gap: 8 }}
        >
          <Button
            loading={orderSummaryBusy === "preview"}
            icon={<EyeOutlined />}
            onClick={() => void runManualOrderSummary("preview")}
          >
            תצוגה מקדימה
          </Button>
          <Button
            loading={orderSummaryBusy === "download"}
            icon={<DownloadOutlined />}
            onClick={() => void runManualOrderSummary("download")}
          >
            הורד PDF
          </Button>
          <Button
            type="primary"
            loading={orderSummaryBusy === "send"}
            icon={<MailOutlined />}
            onClick={() => void runManualOrderSummary("send")}
          >
            שלח ללקוח
          </Button>
        </div>
      </Modal>

      <Modal
        title="שלח קבצים ללקוח"
        open={sendFilesModalOpen}
        onCancel={() => setSendFilesModalOpen(false)}
        okText="שלח ללקוח"
        cancelText="ביטול"
        confirmLoading={sendSaving === "bundle"}
        onOk={() => sendDocuments({ keys: sendFilesSelection })}
        destroyOnClose
        centered
        zIndex={CRM_DETAIL_OVERLAY_Z}
        getContainer={() => document.body}
      >
        <p className="crm-docket__send-files-hint">
          בחרו אילו קבצים לשלוח לכתובות המייל שהוגדרו בתיק הלקוח:
        </p>
        <Checkbox.Group
          className="crm-docket__send-files-checkboxes"
          options={sendFilesCheckboxOptions}
          value={sendFilesSelection}
          onChange={(vals) => setSendFilesSelection(vals)}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        />
        {!extraFiles.length ? (
          <p className="crm-docket__placeholder" style={{ marginTop: 12 }}>
            אין עדיין קבצים נוספים בתיק
            {canUploadExtraFiles
              ? ". אדמין יכול להעלות PDF מתיק הלקוח (ליד התצוגה המקדימה)."
              : "."}
          </p>
        ) : null}
      </Modal>

      <Modal
        title="הוספת תשלום שהתקבל"
        open={paymentAddOpen}
        onCancel={() => setPaymentAddOpen(false)}
        okText="שמירה"
        cancelText="ביטול"
        confirmLoading={paymentEntrySaving}
        onOk={() => void submitPaymentEntry()}
        destroyOnClose
        centered
        zIndex={CRM_DETAIL_OVERLAY_Z}
        getContainer={() => document.body}
      >
        <p className="crm-docket__pay-form-hint">
          נרשם בדוקט CRM בלבד — לא משנה את סכומי ההזמנה במערכת התשלומים.
        </p>
        <div className="crm-docket__pay-form">
          <label className="crm-docket__pay-form-field">
            <span>אמצעי תשלום</span>
            <Select
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentFormMethod}
              onChange={setPaymentFormMethod}
              style={{ width: "100%" }}
            />
          </label>
          <label className="crm-docket__pay-form-field">
            <span>סוג</span>
            <Select
              options={PAYMENT_DIRECTION_OPTIONS}
              value={paymentFormDirection}
              onChange={setPaymentFormDirection}
              style={{ width: "100%" }}
            />
          </label>
          <label className="crm-docket__pay-form-field">
            <span>סכום (₪)</span>
            <Input
              value={paymentFormAmount}
              onChange={(e) => setPaymentFormAmount(e.target.value)}
              placeholder="לדוגמה 10280"
              inputMode="decimal"
            />
          </label>
          <label className="crm-docket__pay-form-field">
            <span>תאריך (אופציונלי)</span>
            <Input
              type="date"
              value={paymentFormDate}
              onChange={(e) => setPaymentFormDate(e.target.value)}
            />
          </label>
          <label className="crm-docket__pay-form-field">
            <span>הערה (אופציונלי)</span>
            <Input.TextArea
              rows={2}
              value={paymentFormNote}
              onChange={(e) => setPaymentFormNote(e.target.value)}
              placeholder="למשל: לפי אישור העברה / PDF"
              maxLength={500}
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}
